import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Instagram, ExternalLink, Sparkles, Play, ShoppingBag, Quote, FileText, Film, BarChart3, Share2, Check, Trophy, Plus, Briefcase, X, Radio } from 'lucide-react';
import InstagramShareModal from '../components/InstagramShareModal';
import { resolveImageUrl } from '../components/ImageUpload';
import VerifiedCheck from '../components/VerifiedCheck';
import HopeBadge from '../components/HopeBadge';
import AthleteBrandBadges from '../components/AthleteBrandBadges';
import { toVideoEmbed, getVideoAspectClass, isDirectVideo, isHudlUrl } from '../utils/videoEmbed';
import YouTubeEmbed from '../components/YouTubeEmbed';
import { safeHref } from '../lib/safeUrl';

// iter346 · Pulls the 11-char YouTube ID out of any watch / share /
// Shorts / /live/ / /embed/ URL so we can hand it to <YouTubeEmbed/>.
// Returns null when the URL isn't a YouTube link.
const _extractYouTubeId = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  const url = raw.trim();
  if (!/youtube\.com|youtu\.be/i.test(url)) return null;
  const patterns = [
    /youtu\.be\/([A-Za-z0-9_-]{6,})/,
    /[?&]v=([A-Za-z0-9_-]{6,})/,
    /youtube\.com\/shorts\/([A-Za-z0-9_-]{6,})/,
    /youtube\.com\/live\/([A-Za-z0-9_-]{6,})/,
    /youtube(?:-nocookie)?\.com\/embed\/([A-Za-z0-9_-]{6,})/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
};
import ShareToStoryButton from '../components/ShareToStoryButton';
import { getPublicOrigin, haptic } from '../lib/nativeBridge';
// iter481 · Compact alias so tab-switch onClick reads clean inline.
const hapticLight = () => haptic('light');
import { peekCache, warmImages, rememberHeroForSlug, preheatProfile, linkHoverProps } from '../lib/nativeCache';
import BusinessInquiriesPill from '../components/athlete/BusinessInquiriesPill';
import { TrackFlytLiveButton } from '../components/live/TrackFlytLive';
import { ShowOnLockScreenButton } from '../components/ShowOnLockScreenButton';
import SaveButton from '../components/SaveButton';
import FollowButton from '../components/FollowButton';
import PhotoLightbox from '../components/PhotoLightbox';
import CareerTimeline from '../components/CareerTimeline';
import RichCareerTimeline from '../components/athlete/RichCareerTimeline';
import AthleteInfoBar from '../components/athlete/AthleteInfoBar';
import AthleteSocialLinks from '../components/athlete/AthleteSocialLinks';
import AthletePlatformLinks from '../components/athlete/AthletePlatformLinks';
import AthletePlatformsModal from '../components/athlete/AthletePlatformsModal';
import AddArticleModal from '../components/athlete/AddArticleModal';
import AthleteAvatarFallback from '../components/athlete/AthleteAvatarFallback';
import ClaimProfileModal from '../components/athlete/ClaimProfileModal';
import ReportBlockMenu from '../components/ReportBlockMenu';
import BasicsEditorModal from '../components/athlete/BasicsEditorModal';
import AthleteProfileHero from '../components/athlete/AthleteProfileHero';
import AthleteQuickStatsGrid from '../components/athlete/AthleteQuickStatsGrid';
import { toast } from 'sonner';
import ContinueReadingBeacon from '../components/ContinueReadingBeacon';
import NetworkVideoModal from '../components/NetworkVideoModal';
import AthleteDigitalHomeSections, { DigitalHomeAnchorNav } from '../components/athlete/AthleteDigitalHomeSections';
import AthleteSectionEditorDrawer from '../components/athlete/AthleteSectionEditorDrawer';
import NetworkProfileTour from '../components/athlete/NetworkProfileTour';
import { useUserAuth } from '../contexts/UserAuthContext';
import FlytLinkPromoCard from '../components/flytlink/FlytLinkPromoCard';
import LiveGamePill from '../components/LiveGamePill';
import PageSkeleton from '../components/PageSkeleton';
import SEOHead from '../components/SEOHead';
import { AppStoreBadge, APP_STORE_URL } from '../components/AppStorePromo';
import { trackAppStoreClick, trackMeta } from '../lib/metaTracking';

const SHARE_DOMAIN = 'flythouse.com';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveSrc = (url) => (url && url.startsWith('/api/')) ? `${BACKEND_URL}${url}` : url;
// A pasted link that's neither an embeddable YouTube/Vimeo/Instagram URL
// nor a direct video file (most commonly a Hudl profile/watch page, which
// blocks iframe embedding and isn't a raw video file either) used to fall
// through to a raw <video src="..."> tag — which the browser can't play,
// rendering its permanent broken-media icon. This renders a themed
// tap-through card instead, matching the pattern already used on the
// public share page for the same non-embeddable-Hudl case.
const VideoLinkFallbackCard = ({ url, title, aspect }) => {
  const hudl = isHudlUrl(url);
  const label = hudl ? 'Hudl' : 'Video';
  const color = hudl ? '#FF6600' : '#3B82F6';
  return (
    <a
      href={safeHref(url)}
      target="_blank"
      rel="noopener noreferrer"
      className={`${aspect} rounded-2xl overflow-hidden ring-1 ring-white/10 flex items-center justify-center text-center px-6`}
      style={{ background: `linear-gradient(135deg, ${color}33, #000 55%, #0a0a0a)` }}
      data-testid="network-video-fallback-link"
    >
      <div>
        <Play className="w-8 h-8 text-white/90 mx-auto mb-2" />
        <p className="text-sm font-bold text-white">{title || `Watch on ${label}`}</p>
        <p className="text-[10px] uppercase tracking-widest text-white/50 mt-1">Opens in new tab</p>
      </div>
    </a>
  );
};

/**
 * AthletePartnersRow — small horizontal row of circular partner badges
 * shown right below the Follow/Get Updates/Share row on the athlete
 * profile card. Reads from `athlete.brand_partners` (managed by admins
 * via the Network tab admin editor). Every athlete gets a "FLYT" badge
 * prepended automatically — it's the house partner and gives every
 * roster page a visual anchor even when no external partners are set
 * yet. Individual admin-added partners render as small round tiles
 * showing either the uploaded logo or the first two letters of the
 * partner label if there's no image.
 */
// BusinessInquiriesPill moved to components/athlete/BusinessInquiriesPill.jsx
// so it can be shared with /l/{slug} (AthleteSharePage).

const BUBBLE_CLASS = 'w-9 h-9 rounded-full inline-flex items-center justify-center overflow-hidden shrink-0 ring-1 ring-white/15 bg-white/5 [&>img]:!w-full [&>img]:!h-full [&>img]:!object-cover [&>img]:!object-center';

const AthletePartnersRow = ({ athlete }) => {
  const admin = athlete?.brand_partners || [];
  // iter134 · Agent lookup — if the athlete is represented we surface
  // an AGENT badge that opens a modal with agency overview + roster
  // + Contact Agent form. Fetches speculatively; 404 = no agent, no
  // badge rendered.
  const [agentInfo, setAgentInfo] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  useEffect(() => {
    if (!athlete?.slug) return;
    let cancelled = false;
    axios.get(`${API}/network/athletes/${athlete.slug}/agent`)
      .then((r) => { if (!cancelled) setAgentInfo(r.data); })
      .catch(() => { /* 404 = no agent, silent */ });
    return () => { cancelled = true; };
  }, [athlete?.slug]);

  // iter101 · Meta ViewContent — high-intent athlete profile view (retargeting).
  useEffect(() => {
    if (!athlete?.slug) return;
    trackMeta('ViewContent', {
      customData: {
        content_type: 'product',
        content_ids: [athlete.slug],
        content_name: athlete.name || athlete.slug,
        content_category: 'athlete_profile',
      },
    });
  }, [athlete?.slug]);

  // Cap to 8 external partners so the row stays a row, not a wrap.
  const partners = [
    { key: 'flyt', label: 'FLYT', house: true },
    // Agent tile — clickable, opens the overview modal.
    ...(agentInfo?.agent ? [{
      key: 'agent',
      label: agentInfo.agent.agency_name || agentInfo.agent.name || 'Agent',
      image: agentInfo.agent.agency_logo || agentInfo.agent.photo,
      accent_color: agentInfo.agent.accent_color,
      onClick: () => setModalOpen(true),
      isAgent: true,
    }] : []),
    // School badge (auto-added when athlete belongs to a school)
    ...(athlete?.school_name || athlete?.school_slug ? [{
      key: 'school',
      label: athlete.school_name || athlete.school_slug,
      image: athlete.school_logo_url,
      url: athlete.school_slug ? `/school-network/${athlete.school_slug}` : undefined,
      internal: true,
      school: true,
    }] : []),
    ...admin.slice(0, 8).map((p, i) => ({
      key: p.url || p.label || `p-${i}`,
      label: p.label || 'Partner',
      image: p.image,
      url: p.url,
    })),
  ];

  const initialsOf = (name) => {
    const parts = String(name || '').trim().split(/\s+/);
    if (!parts.length) return '';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="athlete-partners-row">
      <p className="text-[10px] uppercase tracking-[0.24em] text-gray-500 font-bold shrink-0">Explore</p>
      {partners.map((p) => {
        // iter194 · Uniform bubble sizing — one shared class so every
        // variant (house FLYT badge, agent logo, school logo, admin
        // partners, initials fallback) renders at the exact same
        // 36×36 px circle. Any padding/ring differences before this
        // pass are gone.
        const inner = p.house ? (
          <span
            className={BUBBLE_CLASS}
            style={{
              background: 'linear-gradient(135deg,#0a0a0a 0%,#111827 100%)',
              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
            }}
            title="FLYT — house partner"
          >
            <span className="text-white text-[8px] font-black tracking-tighter">FLYT</span>
          </span>
        ) : p.image ? (
          // iter459v · Image branch renders both the <img> AND an
          // initials fallback in the same bubble. If the image fails
          // to load (404, broken CDN link, cold Cloudinary derivative),
          // the img `onError` handler hides itself and the initials
          // layer beneath is exposed — same look as the no-image
          // partner tiles. Prevents the broken-image glyph Jay saw on
          // partners where the logo URL had gone stale.
          <span
            className={BUBBLE_CLASS + ' relative'}
            style={{ background: 'linear-gradient(135deg,#1e293b 0%,#0f172a 100%)' }}
          >
            <span className="absolute inset-0 flex items-center justify-center text-white text-[9px] font-bold">
              {initialsOf(p.label)}
            </span>
            <img
              loading="lazy"
              decoding="async"
              src={resolveImageUrl(p.image, 160)}
              alt={p.label}
              className="relative w-full h-full object-cover object-center"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </span>
        ) : (
          <span
            className={BUBBLE_CLASS}
            style={{ background: 'linear-gradient(135deg,#1e293b 0%,#0f172a 100%)' }}
            title={p.label}
          >
            <span className="text-white text-[9px] font-bold">{initialsOf(p.label)}</span>
          </span>
        );

        // Agent badge — clickable to open the overview modal.
        if (p.isAgent && p.onClick) {
          return (
            <button
              type="button"
              key={p.key}
              onClick={p.onClick}
              aria-label={`View ${p.label}`}
              className="transition hover:opacity-80 active:scale-95 focus:outline-none rounded-full p-0 leading-none"
              data-testid="athlete-partner-badge-agent"
            >
              {inner}
            </button>
          );
        }
        // If the partner has an outbound URL, wrap it in an anchor so
        // fans can click through directly. FLYT badge is decorative.
        // Internal targets use <Link> (client-side).
        if (!p.house && p.url) {
          const isInternal = p.internal || p.url.startsWith('/');
          if (isInternal) {
            return (
              <Link
                key={p.key}
                to={p.url}
                aria-label={p.label}
                className="transition hover:opacity-80 active:scale-95 rounded-full p-0 leading-none"
                data-testid={`athlete-partner-badge-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {inner}
              </Link>
            );
          }
          return (
            <a
              key={p.key}
              href={safeHref(p.url)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={p.label}
              className="transition hover:opacity-80 active:scale-95 rounded-full p-0 leading-none"
              data-testid={`athlete-partner-badge-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {inner}
            </a>
          );
        }
        return (
          <span
            key={p.key}
            aria-label={p.label}
            className="p-0 leading-none"
            data-testid={`athlete-partner-badge-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {inner}
          </span>
        );
      })}
      {modalOpen && agentInfo?.agent && (
        <AgentOverviewModal
          agent={agentInfo.agent}
          roster={agentInfo.roster || []}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
};

/**
 * AgentOverviewModal · public agency profile + Contact Agent CTA.
 * Rendered when a fan taps the AGENT badge in the Partners row of
 * an athlete's Network profile. Read-only view of the agency: logo,
 * bio, roster grid (all their athletes), plus a Contact form that
 * hits POST /api/network/agents/:id/contact. Keeps the growth loop
 * hot — new athletes discovering the platform can request
 * representation from any existing agent without leaving the athlete
 * profile they landed on.
 */
const AgentOverviewModal = ({ agent, roster, onClose }) => {
  const [contactOpen, setContactOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', sport: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  // iter169 · Signed-in athlete → one-tap "Connect" that files a
  // pending athlete_agent_request into the agent's Requests launchpad
  // (agent approves it there → auto-adds athlete to roster).
  const [connectState, setConnectState] = useState('idle'); // idle | sending | sent | error
  const [connectError, setConnectError] = useState('');

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Name, email and message are required.');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/network/agents/${agent.id}/contact`, form);
      setSent(true);
      toast.success(`Message sent to ${agent.agency_name || agent.name}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send message. Try again.');
    } finally {
      setSending(false);
    }
  };

  const sendConnect = async () => {
    if (connectState === 'sending') return;
    setConnectState('sending');
    setConnectError('');
    try {
      await axios.post(
        `${API}/me/athlete/agent-request`,
        { agent_id: agent.id, note: '' },
        { withCredentials: true },
      );
      setConnectState('sent');
      toast.success(`Request sent to ${agent.agency_name || agent.name}.`);
    } catch (err) {
      setConnectState('error');
      const detail = err?.response?.data?.detail || 'Could not send request.';
      setConnectError(detail);
      // 403 → not a claimed athlete. Point them to the fallback form.
      if (err?.response?.status === 403) {
        toast.info('Only athletes with a claimed profile can send a Connect request. Use the contact form below.');
        setContactOpen(true);
      } else {
        toast.error(detail);
      }
    }
  };

  return (
    <div
      className="fixed inset-0 z-[95] flex items-start justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto"
      style={{
        paddingTop: 'max(1rem, env(safe-area-inset-top))',
        paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="agent-overview-modal"
    >
      <div
        className="w-full max-w-2xl my-auto rounded-3xl bg-neutral-950 ring-1 ring-white/15 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="relative px-6 pt-6 pb-5"
          style={{
            background: `linear-gradient(160deg, ${agent.accent_color || '#22D3EE'}22 0%, transparent 60%)`,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center justify-center"
            aria-label="Close"
            data-testid="agent-overview-close"
          >
            <span className="text-white text-lg leading-none">×</span>
          </button>
          <div className="flex items-start gap-4">
            {agent.agency_logo || agent.photo ? (
              <img
                src={resolveImageUrl(agent.agency_logo || agent.photo, 160)}
                alt=""
                className="w-16 h-16 rounded-2xl object-cover ring-1 ring-white/20 shrink-0"
              />
            ) : (
              <div
                className="w-16 h-16 rounded-2xl inline-flex items-center justify-center ring-1 ring-white/20 shrink-0"
                style={{ background: `${agent.accent_color || '#22D3EE'}33` }}
              >
                <span className="font-black text-xl text-white">{(agent.agency_name || agent.name || 'A')[0]}</span>
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p
                className="text-[10px] uppercase tracking-[0.28em] font-black"
                style={{ color: agent.accent_color || '#22D3EE' }}
              >Sports Agency</p>
              <h3 className="font-display text-2xl uppercase tracking-tight text-white mt-1 leading-tight">
                {agent.agency_name || agent.name}
              </h3>
              <p className="text-[11px] text-white/50 mt-1">{agent.roster_size} athlete{agent.roster_size === 1 ? '' : 's'} on roster</p>
              {agent.bio && (
                <p className="text-sm text-white/70 mt-3 leading-snug">{agent.bio}</p>
              )}
            </div>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="agent-overview-actions">
            {connectState === 'sent' ? (
              <span
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-[11px] font-black uppercase tracking-widest bg-emerald-500/20 ring-1 ring-emerald-400/40 text-emerald-200"
                data-testid="agent-overview-connect-sent"
              >
                ✓ Request sent · awaiting approval
              </span>
            ) : (
              <button
                type="button"
                onClick={sendConnect}
                disabled={connectState === 'sending'}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-[11px] font-black uppercase tracking-widest text-black hover:opacity-90 disabled:opacity-60"
                style={{ background: agent.accent_color || '#22D3EE' }}
                data-testid="agent-overview-connect"
                title="If you're a signed-in athlete, this sends a Connect request straight to the agent's Requests inbox."
              >
                {connectState === 'sending' ? 'Sending…' : 'Connect · Request rep'}
              </button>
            )}
            <button
              type="button"
              onClick={() => setContactOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-[11px] font-black uppercase tracking-widest text-white bg-white/[0.06] ring-1 ring-white/15 hover:bg-white/[0.12]"
              data-testid="agent-overview-contact-toggle"
            >
              {contactOpen ? 'Hide contact form' : 'Contact via email'}
            </button>
          </div>
          {connectError && connectState === 'error' && (
            <p className="text-[11px] text-rose-300 mt-2" data-testid="agent-overview-connect-error">{connectError}</p>
          )}
        </div>

        {/* Contact form (collapsible) */}
        {contactOpen && (
          <div className="border-t border-white/10 px-6 py-5 bg-white/[0.02]" data-testid="agent-contact-form">
            {sent ? (
              <div className="text-center py-4">
                <p className="text-emerald-300 font-black uppercase tracking-widest text-xs">Message sent</p>
                <p className="text-sm text-white/70 mt-2">{agent.agency_name || agent.name} will reach out via the email you provided.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" placeholder="Your name" required
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="px-3 py-2.5 rounded-xl bg-black/40 ring-1 ring-white/10 text-sm text-white focus:ring-white/40 outline-none placeholder-white/30"
                    data-testid="agent-contact-name" />
                  <input type="email" placeholder="Your email" required
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="px-3 py-2.5 rounded-xl bg-black/40 ring-1 ring-white/10 text-sm text-white focus:ring-white/40 outline-none placeholder-white/30"
                    data-testid="agent-contact-email" />
                  <input type="tel" placeholder="Phone (optional)"
                    value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="px-3 py-2.5 rounded-xl bg-black/40 ring-1 ring-white/10 text-sm text-white focus:ring-white/40 outline-none placeholder-white/30"
                    data-testid="agent-contact-phone" />
                  <input type="text" placeholder="Sport (optional)"
                    value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })}
                    className="px-3 py-2.5 rounded-xl bg-black/40 ring-1 ring-white/10 text-sm text-white focus:ring-white/40 outline-none placeholder-white/30"
                    data-testid="agent-contact-sport" />
                </div>
                <textarea placeholder="Tell them a bit about you — level, goals, what you're looking for…" required rows={4}
                  value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-xl bg-black/40 ring-1 ring-white/10 text-sm text-white focus:ring-white/40 outline-none placeholder-white/30 resize-none"
                  data-testid="agent-contact-message" />
                <button type="submit" disabled={sending}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-xs font-black uppercase tracking-widest text-black hover:opacity-90 disabled:opacity-60"
                  style={{ background: agent.accent_color || '#22D3EE' }}
                  data-testid="agent-contact-submit">
                  {sending ? 'Sending…' : 'Send message'}
                </button>
              </form>
            )}
          </div>
        )}

        {/* Roster grid */}
        {roster && roster.length > 0 && (
          <div className="border-t border-white/10 px-6 py-5">
            <p className="text-[10px] uppercase tracking-[0.28em] font-black text-white/40 mb-3">Roster</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {roster.map((a) => (
                <Link
                  key={a.slug}
                  to={`/network/${a.slug}`}
                  className="group rounded-xl overflow-hidden ring-1 ring-white/10 bg-white/[0.03] hover:ring-white/25 transition"
                  data-testid={`agent-roster-tile-${a.slug}`}
                  {...linkHoverProps(a.slug)}
                >
                  <div className="aspect-square bg-black/60 relative">
                    {a.photo || a.network_hero_image || a.flyt_link_photo ? (
                      <img
                        src={resolveImageUrl(a.photo || a.flyt_link_photo || a.network_hero_image, 400)}
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/30 text-xs uppercase tracking-widest">
                        No photo
                      </div>
                    )}
                  </div>
                  <div className="px-3 py-2">
                    <p className="text-[13px] font-bold text-white truncate">{a.name}</p>
                    <p className="text-[10px] text-white/50 truncate">{a.sport || 'Athlete'}{a.team ? ` · ${a.team}` : ''}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * AthleteNowStrip — a rotating one-liner right below the athlete's
 * name that surfaces what's happening RIGHT NOW. Reads from every
 * field we already have on the athlete doc (live_game, stories,
 * brand_partners, team, school) and composes an array of status
 * lines — then crossfades between them every 4.2s so the profile
 * feels alive on first load, not static. Silent (renders nothing)
 * when there's no signal worth showing.
 */
const AthleteNowStrip = ({ athlete }) => {
  const lines = [];

  // Now Strip is now entirely admin-authored. The dedicated LIVE·MLB
  // pill in the athlete header already handles active-game rendering
  // (and does it better — with score, inning, etc.), so we no longer
  // duplicate a "Live now · vs …" line here.
  (athlete?.now_lines || []).forEach((entry) => {
    if (!entry) return;
    if (typeof entry === 'string') {
      const s = entry.trim();
      if (s) lines.push({ dot: '#ffffff', text: s });
    } else {
      const text = String(entry.text || '').trim();
      if (text) lines.push({ dot: entry.color || '#ffffff', text });
    }
  });

  // If there's nothing admin-authored, render nothing at all so the
  // profile doesn't show a stale "New on the network" beat for every
  // athlete forever. Note: hooks run below unconditionally to satisfy
  // the rules-of-hooks — we early-return AFTER them.

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (lines.length <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % lines.length), 4200);
    return () => clearInterval(t);
  }, [lines.length]);

  if (lines.length === 0) return null;

  const current = lines[idx] || lines[0];

  return (
    <div
      className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 ring-1 ring-white/10 text-[11px] sm:text-xs text-gray-300 overflow-hidden max-w-full"
      data-testid="athlete-now-strip"
      aria-live="polite"
    >
      <span
        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{
          backgroundColor: current.dot,
          boxShadow: `0 0 8px ${current.dot}88`,
        }}
      />
      <span
        key={idx}
        className="truncate min-w-0"
        style={{ animation: 'fadeInSoft 480ms ease' }}
      >
        {current.text}
      </span>
    </div>
  );
};

// Compact "Share" pill that sits in the athlete action-chips row next
// to Follow / Get Updates. Tap → opens the IG-style preview modal
// (same component the story share-row uses) so the fan can see exactly
// what their followers will see, copy a clickable link, send to
// Instagram / iMessage / DM, or download a story-ready graphic.
const AthleteSharePill = ({ athlete, isVisionary, isOwner, onGoLive }) => {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState('idle');  // visual feedback after Send to my Story

  // iter816 · Jay: share links should read like flythouse.com/jaxon — a
  // clean, LinkMe-style handle URL — not the /api/share/athletes/... OG
  // endpoint. Known, accepted trade-off: crawlers (iMessage, WhatsApp,
  // Discord, X) don't get server-rendered OG tags at this bare route, so
  // link previews show generic FLYT branding instead of the athlete's
  // photo/name until the static hosting layer can special-case bots here.
  const { igShareUrl, host } = useMemo(() => {
    const winHost = typeof window !== 'undefined' && window.location?.host
      ? window.location.host
      : SHARE_DOMAIN;
    const origin = getPublicOrigin();
    return {
      igShareUrl: `${origin}/${athlete.slug}`,
      host: winHost,
    };
  }, [athlete.slug]);

  const handleOpen = () => {
    // Heads-up for an owner who hasn't gone live yet — the link they're
    // about to share 404s for anyone but them (see get_athlete_profile's
    // owner-only bypass) until they pay to go live. Warn, don't block —
    // they can still generate/copy the link if they want to get ready.
    if (isOwner && !athlete.verified) {
      toast.warning("You're not live yet", {
        description: 'Anyone you share this with will see a blank page until you go live.',
        duration: 6000,
        action: onGoLive ? { label: 'Go Live', onClick: onGoLive } : undefined,
      });
    }
    setOpen(true);
  };

  const subtitle = athlete.network_tagline
    || athlete.story_reason
    || (athlete.team || athlete.school || athlete.organization || '')
    || athlete.bio
    || `On the ${isVisionary ? 'Dreams Visionary' : 'Dreams League'} Network`;
  // iter459f · `network_hero_image` must win over `network_card_image` —
  // the latter is a stale field from legacy imports/cascades that never
  // gets re-synced when the athlete uploads a new cover via the Network
  // profile editor. Every OTHER image lookup in this file (and the
  // backend OG-card renderer) already does hero-first; this one spot
  // was missed, which is why sharing kept showing an old photo.
  const image = resolveSrc(
    athlete.network_hero_image
    || athlete.network_card_image
    || athlete.cover_image
    || athlete.hero_image
    || athlete.portrait
    || athlete.photo
    || athlete.image
  );

  const Icon = state === 'shared' ? Check : Share2;
  const label = state === 'shared' ? 'Sent' : 'Share';
  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/10 ring-1 ring-white/20 backdrop-blur-md text-white text-xs font-bold hover:bg-white/20 transition active:scale-95"
        data-testid="athlete-share-pill"
        aria-label={`Share ${athlete.name}`}
      >
        <Icon className="w-3.5 h-3.5" />
        {label}
      </button>
      <InstagramShareModal
        open={open}
        onClose={() => {
          setOpen(false);
          // Brief "Sent" confirmation pulse on the pill when the modal
          // closes after a successful share action — matches stories.
          setState('shared');
          setTimeout(() => setState('idle'), 1800);
        }}
        shareUrl={igShareUrl}
        title={athlete.name}
        subtitle={subtitle}
        image={image}
        brandLabel={isVisionary ? 'Dreams Visionary' : 'Dreams League'}
        destinationHost={host}
      />
    </>
  );
};



const AthleteNetworkPage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const isVisionary = location.pathname.startsWith('/visionary');
  const navigate = useNavigate();
  // iter358 · Native perf — hydrate athlete + story synchronously from
  // localStorage (populated by swrCache on prior visits) so the profile
  // paints in 0ms on repeat opens. SWR still revalidates in the
  // background so any admin edits show up on the next refresh.
  const _cachedProfile = (!isVisionary && slug)
    ? peekCache(`/api/network/athletes/${slug}/profile`)
    : null;
  const _cachedVisionary = (isVisionary && slug)
    ? peekCache(`/api/network/visionaries/${slug}`)
    : null;
  const [athlete, setAthlete] = useState(
    _cachedProfile?.athlete || _cachedVisionary || null
  );
  const [story, setStory] = useState(_cachedProfile?.hero_story || null);
  const [relatedStories, setRelatedStories] = useState(_cachedProfile?.stories || []);
  const [relatedShorts, setRelatedShorts] = useState(_cachedProfile?.shorts || []);
  const [liveGame, setLiveGame] = useState(_cachedProfile?.live_game || null);
  const [activeTab, setActiveTab] = useState('story'); // story | stories | shorts | stats
  const [showClaimModal, setShowClaimModal] = useState(false);

  // iter364 · Auto-open the Claim & Verify modal when the URL carries
  // ?claim=1 — used by ClaimAndSignupPage's redirect flow so LiveGamePill
  // taps land directly in the canonical 4-step ClaimProfileModal
  // instead of the legacy selfie-only page.
  const [searchParamsForClaim] = useSearchParams();
  useEffect(() => {
    if (searchParamsForClaim.get('claim') === '1' && athlete) {
      setShowClaimModal(true);
    }
  }, [searchParamsForClaim, athlete]);
  const [showPlatformsModal, setShowPlatformsModal] = useState(false);
  const [showAddArticleModal, setShowAddArticleModal] = useState(false);
  // iter454 · Owner tap-to-edit hero photo opens the same Basics editor
  // sheet used from the /profile page so photo changes flow through the
  // existing PUT /me/athlete/profile + Cloudinary mirroring pipeline.
  const [showBasicsModal, setShowBasicsModal] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null); // {title, subtitle, video_url, poster, body, variant}
  // Skip the "loading" state when we have a cached snapshot — the skeleton
  // was the single biggest source of perceived slowness on repeat visits.
  const [loaded, setLoaded] = useState(!!(_cachedProfile?.athlete || _cachedVisionary));
  // iter363 · Transient-error state so a network hiccup on cold load
  // doesn't mis-label the athlete as "not on the network". Bumping
  // `retryTick` re-fires the effect below.
  const [fetchError, setFetchError] = useState(null);
  const [retryTick, setRetryTick] = useState(0);
  // Phase 2A · inline section editor. `editorSection` = null when closed.
  const [editorSection, setEditorSection] = useState(null);
  // iter363 · Preview mode — when an athlete-owner toggles this via the
  // "Preview" pill next to the school pill, every edit affordance
  // hides so they can see EXACTLY what other fans see. The pill sits
  // right next to a "?" helper that explains the toggle. Never
  // visible to non-owners.
  const [previewMode, setPreviewMode] = useState(false);
  const [previewHelpOpen, setPreviewHelpOpen] = useState(false);
  const { user, refreshUser } = useUserAuth();
  // Owner check — the server computes `is_owner` case-insensitively
  // against the caller's session (see /api/network/athletes/{slug}/profile).
  // We prefer that flag because the athlete's `email` is stripped from
  // the public response for SEC-001. Falls back to a same-email client
  // check for older cached responses that don't include the flag yet.
  const isOwnerRaw = useMemo(() => {
    if (athlete?.is_owner === true) return true;
    const uEmail = (user?.email || '').toLowerCase().trim();
    const aEmail = (athlete?.email || '').toLowerCase().trim();
    return !!uEmail && !!aEmail && uEmail === aEmail;
  }, [user?.email, athlete?.email, athlete?.is_owner]);
  // Every `{isOwner && ...}` gate downstream automatically respects
  // preview mode — no fanning out of the flag through the rest of
  // the (very large) component required.
  const isOwner = isOwnerRaw && !previewMode;
  // "Go Live" — self-serve Network publish. iter888 · FREE: one tap
  // flips the profile live, never a paywall. Edit-mode only (isOwner
  // already bakes in "not previewing") — a real visitor, and the owner
  // in Preview, never see this button.
  const [goLiveBusy, setGoLiveBusy] = useState(false);
  const handleGoLiveTap = useCallback(async () => {
    setGoLiveBusy(true);
    try {
      const r = await axios.post(`${API}/me/athlete/push-to-network`, {}, { withCredentials: true });
      if (r.data?.status === 'live') {
        setAthlete((prev) => (prev ? { ...prev, verified: true, network_active: true } : prev));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not go live. Try again.');
    }
    setGoLiveBusy(false);
  }, []);

  // iter668h · "Edit" mode for the "Inside the World" grid — lets the
  // athlete prune photos they'd previously pushed to the Network back
  // off their public profile without leaving the page. `gallerySelected`
  // holds the raw Cloudinary URLs (that's what `approved_gallery`
  // stores). Reset every time the athlete toggles Edit off.
  const [galleryEditMode, setGalleryEditMode] = useState(false);
  const [gallerySelected, setGallerySelected] = useState(() => new Set());
  const [galleryRemoving, setGalleryRemoving] = useState(false);
  // iter668i · Shared FLYT liquid-glass viewer for the "Inside the
  // World" grid. Owners still get the Edit toggle for prune mode —
  // only non-edit taps open the lightbox.
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const handleRemoveSelectedFromGallery = useCallback(async () => {
    if (gallerySelected.size === 0) return;
    setGalleryRemoving(true);
    try {
      const urls = Array.from(gallerySelected);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/me/media/gallery/remove`,
        { urls },
        { withCredentials: true },
      );
      // Optimistically prune from local state so the grid reflects
      // the change immediately — no full re-fetch needed.
      setAthlete((prev) => prev && ({
        ...prev,
        approved_gallery: (prev.approved_gallery || []).filter((u) => !gallerySelected.has(u)),
      }));
      setGallerySelected(new Set());
      // Auto-exit edit mode when the athlete has cleared their entire
      // selection — the empty-state copy in the section handles the
      // next tap.
      setGalleryEditMode(false);
    } catch (err) {
      // Swallow to a tiny inline flash — full toast infra lives higher
      // up in the tree and the athlete can just retry from the pill.
      console.warn('[flyt] remove from gallery failed', err);
    } finally {
      setGalleryRemoving(false);
    }
  }, [gallerySelected]);
  // iter799 · Jay: there was no way at all to pull a video off the public
  // Shorts reel from the Shorts tab itself — the only path was going back
  // into the Media Hub folder and re-tapping the Shorts toggle there.
  // Same edit-mode pattern as "Inside the World" above, but keyed by
  // `asset_id` (what push-to-shorts stores) instead of a raw URL, and only
  // offered on shorts that actually came from the Media Hub — a manually
  // added admin short (Vimeo link, no asset_id) has nothing to toggle.
  const [shortsEditMode, setShortsEditMode] = useState(false);
  const [shortsSelected, setShortsSelected] = useState(() => new Set());
  const [shortsRemoving, setShortsRemoving] = useState(false);
  // iter814 · Jay: "Remove" only ever pulled a short off the public
  // profile — the video stayed safe in the athlete's Media Hub folder.
  // That's real, useful behavior (an athlete taking a stumble-worthy
  // clip down without losing it), so it's kept exactly as-is, just
  // relabeled "Archive" so it reads as the non-destructive option it
  // always was, alongside the new "Delete" (everywhere) below.
  // iter815 · Backend now matches EITHER `asset_id` or the shorts
  // entry's own `id`, so this same call works for a Media-Hub-sourced
  // short (has asset_id) and an admin/demo short (id only) alike.
  const handleArchiveSelectedFromShorts = useCallback(async () => {
    if (shortsSelected.size === 0) return;
    setShortsRemoving(true);
    try {
      const ids = Array.from(shortsSelected);
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/me/media/push-to-shorts`,
        { asset_ids: ids, remove: true },
        { withCredentials: true },
      );
      setRelatedShorts((prev) => prev.filter((s) => !shortsSelected.has(s.asset_id || s.id)));
      setShortsSelected(new Set());
      setShortsEditMode(false);
    } catch (err) {
      console.warn('[flyt] archive from shorts failed', err);
    } finally {
      setShortsRemoving(false);
    }
  }, [shortsSelected]);
  // iter814 · Jay: athletes had no way to delete a Shorts video outright
  // from the Shorts tab itself — only archive (public-only) or a trip
  // back into the Media Hub folder. Reuses the SAME endpoint the folder
  // page's own delete already calls (`/me/media/folder/remove`), which
  // untags the athlete from the asset and pulls it from every gallery
  // (shorts included) in one call — genuinely gone everywhere for this
  // athlete, not just off the public profile. Confirmed once, in plain
  // language, before it fires — there's no undo.
  // iter815 · An admin/demo short has no underlying media_asset to
  // delete — there's nothing further than the shorts array entry
  // itself, so for those "Delete" and "Archive" both just pull that
  // entry (same push-to-shorts call), split from the asset-backed
  // ones below by whichever field each selected short actually has.
  const [shortsDeleting, setShortsDeleting] = useState(false);
  const handleDeleteSelectedShorts = useCallback(async () => {
    if (shortsSelected.size === 0) return;
    const n = shortsSelected.size;
    if (!window.confirm(
      `Delete ${n} short${n === 1 ? '' : 's'}? This deletes everywhere — your public profile AND your Media Hub folder (where applicable). This cannot be undone.`,
    )) return;
    setShortsDeleting(true);
    try {
      const selected = relatedShorts.filter((s) => shortsSelected.has(s.asset_id || s.id));
      const assetIds = selected.filter((s) => s.asset_id).map((s) => s.asset_id);
      const shortsOnlyIds = selected.filter((s) => !s.asset_id).map((s) => s.id);
      if (assetIds.length) {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/me/media/folder/remove`,
          { asset_ids: assetIds },
          { withCredentials: true },
        );
      }
      if (shortsOnlyIds.length) {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/me/media/push-to-shorts`,
          { asset_ids: shortsOnlyIds, remove: true },
          { withCredentials: true },
        );
      }
      setRelatedShorts((prev) => prev.filter((s) => !shortsSelected.has(s.asset_id || s.id)));
      setShortsSelected(new Set());
      setShortsEditMode(false);
    } catch (err) {
      console.warn('[flyt] delete short failed', err);
    } finally {
      setShortsDeleting(false);
    }
  }, [shortsSelected, relatedShorts]);

  // iter519 · Deduped Articles list — shared between the sticky tab
  // count and the panel body so both surfaces always agree on "how
  // many articles". Previously the tab count read `relatedStories.length`
  // only (ignoring press) AND the panel concatenated press + stories
  // without dedupe. Two bugs surfaced:
  //   1) Athlete adds a press link → count stays at 0 (count ignored press)
  //   2) Athlete adds a FLYT internal story via the modal → the same
  //      story ALSO auto-matches by name in `relatedStories`, so
  //      combined shows TWO identical cards on the public view.
  // Fix: build the merged list once, dedupe on story slug (press wins
  // because the athlete explicitly curated it), and read `.length` from
  // this single source of truth.
  const articleItems = useMemo(() => {
    const pressItems = (athlete?.press || []).map((p) => {
      // Extract a story slug from press URLs that point at internal
      // Flythouse stories so we can dedupe against auto-matched
      // `relatedStories`. Any URL shaped `.../stories/{slug}` counts.
      let inlineSlug = null;
      try {
        const path = new URL(p.url, 'https://x.example').pathname;
        const m = path.match(/\/stories\/([a-z0-9-]+)\/?$/i);
        if (m) inlineSlug = m[1];
      } catch { /* malformed URL → treat as external press */ }
      return {
        kind: 'press',
        key: `press-${p.url}`,
        href: p.url,
        external: !inlineSlug,
        image: p.thumbnail,
        outlet: p.outlet,
        headline: p.headline || p.url,
        date: p.date,
        slug: inlineSlug,
      };
    });
    const pressSlugs = new Set(pressItems.map((p) => p.slug).filter(Boolean));
    const storyItems = (relatedStories || [])
      // Skip stories the athlete already curated as press — same card
      // would otherwise render twice on the wall.
      .filter((s) => !pressSlugs.has(s.slug))
      .map((s) => ({
        kind: 'story',
        key: `story-${s.id || s.slug}`,
        href: `/stories/${s.slug}`,
        external: false,
        image: s.thumbnail || s.cover_image,
        outlet: s.category || s.channel || 'Flythouse',
        headline: s.name,
        date: null,
        subtitle: s.subtitle,
        slug: s.slug,
      }));
    return [...pressItems, ...storyItems];
  }, [athlete?.press, relatedStories]);

  // iter473 · Network Profile onboarding tour. Fires exactly once per
  // account the first time an approved athlete lands on their OWN
  // /network/{slug} page (three approval paths converge here):
  //   1) Anonymous claim → founder-approved → account linked
  //   2) Athlete signs up → pays $4.99 → Push to Network → approved
  //   3) Founding Athlete (free lifetime) → Push to Network → approved
  // Gated on isOwnerRaw (not isOwner) so entering Preview mid-tour
  // doesn't dismiss it; skipped in previewMode entirely so a re-visit
  // in preview never re-arms it. `has_seen_network_tour` is a bool
  // stored on public_users — set via POST /api/me/network-tour/complete.
  const [tourOpen, setTourOpen] = useState(false);
  // Once the tour has fired (completed OR skipped) in the current
  // session we never re-open it, even if `athlete` re-hydrates before
  // the backend flag lands in a fresh /api/me hydrate.
  const tourClosedRef = useRef(false);
  // iter485 · Strict athlete-only gate for the network profile tour.
  // Recruiter / school / agent / brand personas can occasionally
  // trigger the loose `isOwnerRaw` (server may flag them as an owner
  // when an email happens to match, or when they preview a claimed
  // profile). The tour is an ATHLETE-only "welcome to the network"
  // moment, so we require BOTH:
  //   1. The signed-in user's linked_athlete.slug matches this athlete
  //   2. That link is verified (i.e. this account IS the athlete —
  //      not just a pending claim)
  // Non-athlete viewers never see the tour, exactly as the user asked.
  const linkedSlug = user?.linked_athlete?.slug || null;
  const linkedVerified = !!user?.linked_athlete?.verified;
  const isTourEligible = !!(
    linkedSlug
    && athlete?.slug
    && String(linkedSlug).toLowerCase() === String(athlete.slug).toLowerCase()
    && linkedVerified
  );
  useEffect(() => {
    if (!isTourEligible) return;
    if (previewMode) return;
    if (user.has_seen_network_tour) return;
    if (tourClosedRef.current) return;
    const t = setTimeout(() => setTourOpen(true), 700);
    return () => clearTimeout(t);
  }, [isTourEligible, user, previewMode]);

  const finishTour = useCallback(async () => {
    tourClosedRef.current = true;
    setTourOpen(false);
    try {
      await axios.post(`${API}/me/network-tour/complete`);
      // Re-hydrate the auth-context user so `has_seen_network_tour`
      // lands in memory. Prevents re-arm if the user navigates back
      // to this page in the same session before /api/me is refetched.
      refreshUser?.().catch(() => {});
    } catch (_) { /* silent — worst case tour re-fires next visit */ }
  }, [refreshUser]);

  const tourSteps = useMemo(() => ([
    {
      id: 'photo',
      selector: '[data-testid="athlete-hero-photo-target"]',
      title: 'Your Photo',
      message: 'Add your profile picture — tap to upload, drag to reposition.',
    },
    {
      id: 'basics',
      selector: '[data-testid="athlete-hero-pills"]',
      title: 'The Basics',
      message: 'Add your sport, position, and pro level.',
    },
    {
      id: 'metrics',
      selector: '[data-testid="athlete-quick-stats-wrap"]',
      title: 'Key Metrics',
      message: 'Add your key metrics — 40 time, GPA, and other performance stats.',
    },
    {
      id: 'platforms',
      selector: '[data-testid="edit-platforms-btn"]',
      title: 'Platforms',
      message: 'Add your platforms — Hudl, Opendorse, and more.',
    },
    {
      id: 'preview',
      selector: '[data-testid="athlete-preview-toggle"]',
      title: 'Preview',
      message: 'Hit Preview to see how your profile looks to others on the network.',
    },
    {
      id: 'milestones',
      selector: '[data-testid="digital-home-milestones"]',
      title: 'Milestones',
      message: 'Add your milestones.',
    },
    {
      id: 'recruiting',
      selector: '[data-testid="digital-home-recruiting"]',
      title: 'Recruiting & Academics',
      message: 'Add your recruiting information and school academics.',
    },
    {
      id: 'articles',
      selector: '[data-testid="add-article-btn"]',
      title: 'Articles',
      message: 'Add articles.',
      // The Add Article button only mounts when the Stories tab is
      // active — switch to it before we measure so the anchor exists.
      beforeEnter: () => setActiveTab('stories'),
    },
  ]), []);

  useEffect(() => {
    (async () => {
      try {
        if (isVisionary) {
          // Visionaries use the legacy single-record endpoint
          const r = await axios.get(`${API}/network/visionaries/${slug}`);
          setAthlete(r.data);
          if (r.data.network_story_slug) {
            try {
              const s = await axios.get(`${API}/stories/${r.data.network_story_slug}`);
              setStory(s.data);
            } catch (e) { console.debug('[AthleteNetworkPage] story lookup skipped:', e?.message); }
          }
        } else {
          // Athletes get the rich profile aggregator (athlete + stories + shorts)
          // — consumes the index.html deep-link prefetch when present.
          const _pf = (typeof window !== 'undefined' && window.__FLYT_PREFETCH && window.__FLYT_PREFETCH.path === `/${slug}`)
            ? window.__FLYT_PREFETCH.promise : null;
          if (_pf) window.__FLYT_PREFETCH = null;
          const r = _pf
            ? await _pf.then((d) => ({ data: d })).catch(() => axios.get(`${API}/network/athletes/${slug}/profile`))
            : await axios.get(`${API}/network/athletes/${slug}/profile`);
          setAthlete(r.data.athlete);
          setRelatedStories(r.data.stories || []);
          setRelatedShorts(r.data.shorts || []);
          setLiveGame(r.data.live_game || null);
          // iter358 · Native perf — warm hero + related-story cover
          // images the moment the profile response arrives so the
          // rotation into the hero (and any card tap-through) never
          // waits on a fresh HTTP round-trip. Also stash the hero URL
          // so the next launch of this profile paints the image
          // straight from the WebView disk cache.
          try {
            const a = r.data.athlete || {};
            const hero = a.network_hero_image || a.network_card_image || a.photo || a.flyt_link_photo;
            if (hero) rememberHeroForSlug(slug, hero);
            warmImages([
              a.network_hero_image,
              a.network_card_image,
              a.photo,
              a.flyt_link_photo,
              ...(r.data.stories || []).slice(0, 6).map((s) => s.cover_image || s.hero_image),
              ...(r.data.shorts || []).slice(0, 4).map((s) => s.cover_image || s.hero_image),
            ].filter(Boolean));
            // Prefetch the top 3 related athlete profiles so tap-through
            // from the "related" surfaces feels instant.
            (r.data.athlete?.related_slugs || []).slice(0, 3).forEach(preheatProfile);
          } catch { /* warmers must never break the render */ }
          // iter346 · Native perf — profile endpoint now inlines the
          // linked hero story so we don't need a second round-trip.
          // Legacy fallback preserved for older cached payloads.
          if (r.data.hero_story) {
            setStory(r.data.hero_story);
          } else if (r.data.athlete?.network_story_slug) {
            try {
              const s = await axios.get(`${API}/stories/${r.data.athlete.network_story_slug}`);
              setStory(s.data);
            } catch (e) { console.debug('[AthleteNetworkPage] story lookup skipped:', e?.message); }
          }
        }
      } catch (e) {
        // iter363 · Differentiate a genuine 404 ("athlete really isn't
        // on the network") from a transient failure (5xx, timeout,
        // aborted request, network hiccup). Previously ANY error set
        // athlete=null which showed the misleading "Not on the network
        // yet" screen even when the athlete WAS on the network — user
        // just hit a bad-luck load. Now we only null out athlete on a
        // clear 404; transient errors surface a retryable error state.
        const status = e?.response?.status;
        console.debug('[AthleteNetworkPage] athlete fetch failed:', status, e?.message);
        if (status === 404) {
          setAthlete(null);
          setFetchError(null);
        } else {
          setFetchError({ status: status || 0, message: e?.message || 'Network error' });
          // Leave `athlete` undefined so the skeleton keeps showing —
          // combined with the retry button in the error UI below, the
          // user gets an accurate "temporary — tap to retry" affordance
          // instead of a false "not on network" verdict.
        }
      } finally { setLoaded(true); }
    })();
  }, [slug, isVisionary, retryTick]);

  // iter535 · Claim-approval instant refresh. UserAuthContext dispatches
  // `flyt:claim-approved` the moment /me flips to `is_verified_athlete`.
  // Bump `retryTick` so the fetch effect above re-runs and this profile
  // reloads from the server (SWR cache was purged in the same handler).
  // Closes the confusing window where the walkthrough opens but the
  // profile pill still says UNCLAIMED for a minute or two.
  useEffect(() => {
    const onApproved = (e) => {
      if (!slug) return;
      const evtSlug = e?.detail?.slug;
      // Fire when the approval targeted this exact athlete OR when the
      // event carries no slug (defensive — refetch anyway; the fetch is
      // cheap and the UX cost of a stale pill is much worse).
      if (!evtSlug || evtSlug === slug) {
        setRetryTick((n) => n + 1);
      }
    };
    window.addEventListener('flyt:claim-approved', onApproved);
    return () => window.removeEventListener('flyt:claim-approved', onApproved);
  }, [slug]);

  // Fire a click beacon if this landing came from a shared link
  // (carries `?s=<token>`). Best-effort — we don't await it. Tracked
  // via a ref so React StrictMode double-mounts (or router re-renders)
  // never double-count a single click.
  const beaconedTokens = useRef(new Set());
  useEffect(() => {
    if (!slug) return;
    const params = new URLSearchParams(location.search || '');
    const tok = params.get('s');
    if (!tok) return;
    if (beaconedTokens.current.has(tok)) return;
    beaconedTokens.current.add(tok);
    axios.post(`${API}/share/click/${encodeURIComponent(tok)}`, {
      path: location.pathname,
      referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
    }, { withCredentials: true }).catch(() => { /* silent */ });
  }, [slug, location.pathname, location.search]);

  // iter363 · Transient fetch error — show a retry state instead of
  // the misleading "Not on the network yet" screen. Only reached when
  // the request failed with something OTHER than a clean 404.
  if (loaded && fetchError && !athlete) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4" data-testid="athlete-network-error">
        <div className="text-center max-w-md">
          <p className="font-display text-2xl uppercase mb-2">Couldn&apos;t load profile</p>
          <p className="text-gray-400 text-sm mb-6">
            {fetchError.status ? `Request failed (${fetchError.status}).` : 'Network hiccup.'} Tap retry — the athlete might still be on the network.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => { setFetchError(null); setLoaded(false); setRetryTick((n) => n + 1); }}
              className="btn-pill bg-white text-black"
              data-testid="athlete-network-retry-btn"
            >
              Retry
            </button>
            <button
              onClick={() => navigate(isVisionary ? '/visionary/network' : '/')}
              className="btn-pill bg-white/10 text-white ring-1 ring-white/20"
              data-testid="athlete-network-back-from-error"
            >
              Back to Network
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loaded && !athlete) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <p className="font-display text-2xl uppercase mb-2">Not on the network yet</p>
          <p className="text-gray-400 text-sm mb-6">This {isVisionary ? 'visionary' : 'athlete'} doesn&apos;t have a Network home page yet.</p>
          <button onClick={() => navigate(isVisionary ? '/visionary/network' : '/')} className="btn-pill bg-white text-black">Back to Network</button>
        </div>
      </div>
    );
  }
  // While the athlete API call is in flight, render a profile-shaped
  // skeleton instead of a blank screen — same layout as the real page
  // so the swap-in doesn't feel like a full re-shuffle.
  if (!athlete) return <PageSkeleton variant="profile" />;

  const heroImage = athlete.network_hero_image || athlete.photo;
  const body = athlete.network_body || athlete.bio;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="athlete-network-page">
      {athlete?.is_mockup && (
        <div className="sticky top-0 z-50 backdrop-blur-xl bg-blue-600/20 border-b border-white/10 px-4 py-2.5 flex items-center justify-between gap-3" data-testid="mockup-not-live-banner">
          <p className="text-[11px] sm:text-xs text-white/90 font-semibold leading-tight">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 mr-1.5 align-middle" />
            Preview — not live yet. This is the network profile we built for you.
          </p>
          <button
            type="button"
            onClick={() => setShowClaimModal(true)}
            className="shrink-0 px-4 py-1.5 rounded-full bg-white text-black text-xs font-bold hover:bg-gray-100 transition"
            data-testid="mockup-claim-now-btn"
          >
            Claim now
          </button>
        </div>
      )}
      {/* iter459s + iter483 · Landscape iPad spacing.
          Previously clamped the whole page to max-w-3xl (768px) mx-auto
          which turned iPad into a stretched mobile-column view — Jay
          asked for a proper tablet-native layout instead. We now bump
          the ceiling responsively:
            • Mobile (<768px)      → no visible cap (max-w-3xl is a no-op)
            • iPad portrait (md+)  → max-w-5xl (1024px) with md:px-8 gutter
            • iPad Pro / landscape → max-w-6xl (1152px) with lg:px-10 gutter
          The hero photo, stats grid and story columns finally use the
          tablet's real estate without stretching text lines past a
          readable measure (~72-92ch). */}
      <div className="max-w-3xl md:max-w-5xl lg:max-w-6xl mx-auto">
      {/* SEOHead injects a per-athlete canonical + unique meta description
          via the /api/seo/meta backend so every /network/{slug} page
          has its own SEO surface — no shared boilerplate. */}
      <SEOHead
        title={`${athlete.name || ''} · ${athlete.position || ''}${athlete.position && (athlete.school_name || athlete.school) ? ' · ' : ''}${athlete.school_name || athlete.school || ''}${(athlete.position || athlete.school_name || athlete.school) ? ' · ' : ''}Flythouse`.replace(/\s·\s·/g, ' ·')}
        description={athlete.bio_short
          || (athlete.name
              ? `${athlete.name}${athlete.position ? ' — ' + athlete.position : ''}${(athlete.school_name || athlete.school) ? ' at ' + (athlete.school_name || athlete.school) : ''}. Verified career profile, film, stats, and press on Flythouse.`
              : undefined)}
        image={heroImage || undefined}
      />
      <ContinueReadingBeacon
        slug={athlete.slug}
        title={athlete.name}
        channelLabel={isVisionary ? 'Dreamers Network' : 'Dreams League · Network'}
        url={`${isVisionary ? '/visionary/network' : '/network'}/${athlete.slug}`}
        image={resolveImageUrl(athlete.network_hero_image || athlete.photo)}
      />
      {/* === iter454 · REDESIGNED PROFILE HERO ===
          Full-bleed photo + dark gradient overlay + oversized name block.
          Owner-only affordances (preview toggle, tap-to-change photo)
          are hoisted via props so viewers see the same clean layout. */}
      <AthleteProfileHero
        athlete={athlete}
        isOwner={isOwner}
        isVisionary={isVisionary}
        backHref={isVisionary ? '/visionary/network' : '/'}
        onEditPhoto={() => setShowBasicsModal(true)}
        onSaved={(patch) => setAthlete((a) => ({ ...(a || {}), ...patch }))}
        actions={
          <>
            {/* iter454.4 · Compact single-row action pills — smaller
                padding, smaller icons, smaller text so Follow / Get
                Updates / Share fit on a single line inside the hero's
                left column (matches the Jaxon mockup). Uses arbitrary
                Tailwind variants to override the child button padding
                without touching the shared SaveButton/FollowButton
                components used elsewhere.
                iter515 · Added `max-w-full` + `min-w-0` so children
                shrink instead of pushing off-screen; Report menu now
                lives on its own row on narrow viewports.  */}
            <div className="flex flex-wrap items-center gap-1 max-w-full min-w-0 [&_button]:!px-2 [&_button]:!py-0.5 [&_button]:!text-[10px] [&_button]:!gap-1 [&_button]:!font-bold [&_button_svg]:!w-2.5 [&_button_svg]:!h-2.5">
              <SaveButton type="athletes" id={athlete.id} variant="pill" label="Follow" savedLabel="Following" />
              <FollowButton athleteId={athlete.id} athleteName={athlete.name} variant="pill" theme="dark" />
              <AthleteSharePill athlete={athlete} isVisionary={isVisionary} isOwner={isOwner} onGoLive={handleGoLiveTap} />
              {liveGame && liveGame.athlete_side && (
                <LiveGameBadge game={liveGame} athleteName={athlete.name} />
              )}
              {/* iter512 · Apple 1.2 UGC — Report / Block available on
                  every visitor-visible profile. Hidden for the owner
                  themselves so they can't report their own profile.
                  iter516l · Uses default align='left' (dropdown opens
                  RIGHT). Explicit right-alignment was pushing the
                  dropdown off-screen on narrow iPhones. */}
              {!isOwner && (
                <ReportBlockMenu
                  kind="athlete"
                  targetId={athlete.id}
                  targetUserId={athlete.claimed_user_id || athlete.user_id}
                  targetLabel={athlete.name}
                  label="Report"
                />
              )}
            </div>
            <ShowOnLockScreenButton athlete={athlete} className="max-w-full" />
            <AthleteSocialLinks athlete={athlete} isOwner={isOwner} />
          </>
        }
        topRight={
          <>
            {isOwnerRaw && (
              <div className="relative inline-flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setPreviewHelpOpen((v) => !v)}
                  className="w-6 h-6 rounded-full bg-black/55 ring-1 ring-white/25 inline-flex items-center justify-center hover:bg-black/75 transition backdrop-blur-md"
                  aria-label="What is preview view?"
                  data-testid="athlete-preview-help"
                >
                  <span className="text-white text-[11px] font-bold leading-none">?</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewMode((v) => !v)}
                  className={`inline-flex items-center gap-1.5 pl-2.5 pr-3 py-1 rounded-full ring-1 text-[10px] uppercase tracking-widest font-black transition backdrop-blur-md ${
                    previewMode
                      ? 'bg-blue-500/30 ring-blue-400/60 text-blue-100'
                      : 'bg-black/55 ring-white/20 text-white/90 hover:bg-black/75'
                  }`}
                  aria-pressed={previewMode}
                  data-testid="athlete-preview-toggle"
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${previewMode ? 'bg-blue-400 animate-pulse' : 'bg-white/50'}`} />
                  {previewMode ? 'Previewing' : 'Preview'}
                </button>
                {previewHelpOpen && (
                  <div
                    className="absolute right-0 top-9 w-64 rounded-2xl bg-black/95 ring-1 ring-white/15 shadow-2xl px-3.5 py-3 text-[11px] text-white/85 leading-relaxed backdrop-blur-md z-30"
                    data-testid="athlete-preview-help-popover"
                  >
                    Only you see the edit view of your network profile.
                    Hit <span className="font-bold text-blue-300">Preview</span> to see how others see it.
                    <button
                      type="button"
                      onClick={() => setPreviewHelpOpen(false)}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white/15 ring-1 ring-white/25 inline-flex items-center justify-center text-white/90 hover:bg-white/25"
                      aria-label="Close"
                      data-testid="athlete-preview-help-close"
                    >
                      <span className="text-[9px] font-bold leading-none">×</span>
                    </button>
                  </div>
                )}
              </div>
            )}
            {(athlete.school_slug || athlete.school_name) && (
              <Link
                to={athlete.school_slug ? `/school-network/${athlete.school_slug}` : '#'}
                className="inline-flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-full bg-black/55 ring-1 ring-white/20 hover:bg-black/75 transition backdrop-blur-md"
                data-testid="athlete-school-pill"
                aria-label={`View all athletes at ${athlete.school_name}`}
              >
                {athlete.school_logo_url ? (
                  <img loading="lazy" src={resolveImageUrl(athlete.school_logo_url, 80)} alt=""
                    className="w-5 h-5 rounded-full object-cover ring-1 ring-white/30 flex-shrink-0" />
                ) : (
                  <span
                    className="w-5 h-5 rounded-full inline-flex items-center justify-center text-[8px] font-black text-white ring-1 ring-white/30 flex-shrink-0"
                    style={{ background: athlete.school_primary_color || '#8B5CF6' }}>
                    {(athlete.school_name || 'S').slice(0, 1)}
                  </span>
                )}
                <span className="text-[10px] uppercase tracking-widest text-white font-bold truncate max-w-[110px]">
                  {athlete.school_name}
                </span>
              </Link>
            )}
          </>
        }
      />

      {/* iter454.2 · Quick Stats — thin single-row strip of 6 tiles
          sitting right under the hero card so it reads as one unit,
          exactly like the Jaxon mockup. Full width of the hero. */}
      <div className="px-3 sm:px-5 -mt-3 pb-3 relative z-10" data-testid="athlete-quick-stats-wrap">
        <AthleteQuickStatsGrid
          athlete={athlete}
          isOwner={isOwner}
          onSaved={(patch) => setAthlete((a) => ({ ...(a || {}), ...patch }))}
        />
      </div>

      <div className="max-w-3xl md:max-w-5xl lg:max-w-6xl mx-auto px-4 sm:px-6 md:px-8 lg:px-10 pt-2 pb-4 space-y-3" data-testid="athlete-profile-meta">
        {/* Tagline — if the athlete set a network_tagline it sits under
            the stats grid like a lower-third caption. */}
        {athlete.network_tagline && (
          <p className="font-italic text-sm text-gray-300 leading-snug line-clamp-2" data-testid="athlete-network-tagline">
            {athlete.network_tagline}
          </p>
        )}
        {/* Brand pills — every brand_networks slug this athlete is tagged
            into. Renders as inline chips so multiple brands fit cleanly. */}
        <AthleteBrandBadges
          athlete={athlete}
          variant="inline"
          size="md"
          exclude={athlete.hope ? ['hope'] : []}
        />

        {/* Now Strip — rotating admin-authored one-liners. */}
        <AthleteNowStrip athlete={athlete} />

        {/* Floating "Track FLYT Live" only for Shape A payloads. */}
        {liveGame && liveGame.athlete_side && (
          <TrackFlytLiveButton
            variant="floating"
            game={{
              game_id: liveGame.game_id || liveGame.id || `${athlete.slug}-${liveGame.league || 'game'}`,
              team_slug: athlete.slug,
              team_name: (liveGame.team && (liveGame.team.shortName || liveGame.team.name)) || athlete.team || '',
              opponent: (liveGame.opp && (liveGame.opp.shortName || liveGame.opp.name)) || '',
              kickoff_iso: liveGame.kickoff_iso || liveGame.start_time || '',
              home: (liveGame.team && (liveGame.team.shortName || liveGame.team.name)) || '',
              away: (liveGame.opp && (liveGame.opp.shortName || liveGame.opp.name)) || '',
            }}
          />
        )}

        {/* Phase 1 · Athlete-first platform links — Hudl / MaxPreps /
            Opendorse. Owners see a small pill to add / edit. */}
        <div className="flex flex-wrap items-center gap-2">
          <AthletePlatformLinks athlete={athlete} className="mt-1" />
          {isOwner && (
            <button
              onClick={() => setShowPlatformsModal(true)}
              className="mt-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ring-1 ring-cyan-400/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-200 text-[11px] font-black uppercase tracking-widest transition"
              data-testid="edit-platforms-btn"
              aria-label="Add or edit Hudl, MaxPreps, and Opendorse links"
            >
              <Plus className="w-3 h-3" />
              {athlete.hudl_url || athlete.maxpreps_url || athlete.opendorse_url ? 'Edit Platforms' : 'Add Platforms'}
            </button>
          )}
        </div>

        {/* "Go Live" — self-serve Network publish. Edit-mode only
            (isOwner already excludes Preview mode) so a real visitor,
            and the owner while previewing, never see this. Everything
            on this page is already free to edit; this is the one gate
            standing between a draft profile and a shareable, publicly
            discoverable one. */}
        {isOwner && (
          athlete.verified ? (
            <div
              className="rounded-xl bg-emerald-500/10 ring-1 ring-emerald-400/30 p-3 flex items-center gap-2.5"
              data-testid="network-you-are-live"
            >
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500/20 ring-1 ring-emerald-400/40 flex-shrink-0">
                <Radio className="w-3.5 h-3.5 text-emerald-300" />
              </span>
              <p className="text-[11px] sm:text-xs font-black uppercase tracking-widest text-emerald-200">
                You&apos;re Live
              </p>
            </div>
          ) : (
            <div
              className="rounded-xl bg-blue-500/10 ring-1 ring-blue-400/30 p-3 flex items-center justify-between gap-3"
              data-testid="network-go-live"
            >
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-widest text-blue-300 font-bold">Not live yet</p>
                <p className="text-xs text-white/85 mt-0.5 leading-snug">
                  Your profile is private until you go live. Go live free to share it anywhere and get discovered.
                </p>
              </div>
              <button
                type="button"
                onClick={handleGoLiveTap}
                disabled={goLiveBusy}
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-500 hover:bg-blue-400 disabled:opacity-60 text-white text-[11px] font-black uppercase tracking-widest transition"
                data-testid="go-live-btn"
              >
                <Radio className="w-3.5 h-3.5" />
                {goLiveBusy ? 'Going live…' : 'Go Live'}
              </button>
            </div>
          )
        )}

        {/* Claim Profile CTA — visible only for orphan roster-sync
            athletes (unclaimed + not auto-provisioned). */}
        {(!athlete.claim_status
            || athlete.claim_status === 'public'
            || athlete.claim_status === 'unclaimed')
          && !athlete.public_user_id
          && !athlete.verified
          && !athlete.auto_provisioned && (
          <div className="rounded-xl bg-blue-500/10 ring-1 ring-blue-400/30 p-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-blue-300 font-bold">Unclaimed</p>
              <p className="text-xs text-white/85 mt-0.5 leading-snug">
                This profile hasn&apos;t been claimed. Is this you? Verify your identity to connect it.
              </p>
            </div>
            <button
              onClick={() => setShowClaimModal(true)}
              className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white text-black text-[11px] font-black uppercase tracking-widest hover:bg-blue-100 transition"
              data-testid="claim-profile-btn"
            >
              Claim now
            </button>
          </div>
        )}
        {athlete.claim_status === 'claimed' && (
          <div className="rounded-xl bg-amber-500/10 ring-1 ring-amber-400/30 p-3">
            <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Claim pending review</p>
            <p className="text-xs text-white/85 mt-0.5 leading-snug">
              Someone submitted a claim for this profile. Our team is reviewing — check back shortly.
            </p>
          </div>
        )}

        {/* iter459 · Founder-only kill-switch — persistent on EVERY
            player profile so if the claim → approve → link flow ever
            wedges again (DJ Marquardt-style: verified athlete row
            exists but athlete signed in and saw a duplicate auto-
            provisioned row instead), Jay can nuke the state without
            digging through the admin console. Runs the same
            `/admin/athletes/{slug}/reset-claim` that now fully
            unverifies + detaches every user pointing at the slug. */}
        {user?.is_founder && (
          <div
            className="rounded-xl bg-rose-500/8 ring-1 ring-rose-400/25 p-3"
            data-testid="founder-controls"
          >
            <p className="text-[10px] uppercase tracking-widest text-rose-300 font-black">Founder controls</p>
            <p className="text-[11px] text-white/70 mt-0.5 leading-snug">
              Reset drops this athlete back to <span className="text-white/90">public / unclaimed</span> and
              detaches any user linked to it. Use this if a claim → approval got stuck.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm(`Reset claim for ${athlete.name}? This wipes the claim, verifications, and detaches any linked user. Cannot be undone.`)) return;
                  try {
                    const r = await axios.post(`${API}/admin/athletes/${athlete.slug}/reset-claim`, {}, { withCredentials: true });
                    const removed = r?.data?.removed || {};
                    toast.success(`Reset · claims:${removed.claims || 0} · ids:${removed.id_verifications || 0} · users:${removed.users_detached || 0}`);
                    setTimeout(() => window.location.reload(), 700);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Reset failed');
                  }
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-bold bg-rose-500/20 hover:bg-rose-500/30 ring-1 ring-rose-400/40 text-rose-100 transition"
                data-testid="founder-reset-claim-btn"
              >
                Reset claim
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm('Heal every approved-but-orphan identity verification (link users to their verified athlete rows). Safe to run repeatedly.')) return;
                  try {
                    const r = await axios.post(`${API}/admin/verifications/backfill-orphans`, {}, { withCredentials: true });
                    const d = r?.data || {};
                    toast.success(`Backfill · healed:${d.healed || 0} · skipped:${d.skipped || 0} · merged:${d.merged_drafts || 0}`);
                    setTimeout(() => window.location.reload(), 900);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Backfill failed');
                  }
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-bold bg-white/8 hover:bg-white/15 ring-1 ring-white/20 text-white/85 transition"
                data-testid="founder-backfill-orphans-btn"
              >
                Backfill orphan approvals
              </button>
            </div>
          </div>
        )}

        {/* Partners row + Business inquiries pill. */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <AthletePartnersRow athlete={athlete} />
          <BusinessInquiriesPill athlete={athlete} />
        </div>

        {athlete.bio && (
          <p className="text-[11px] sm:text-xs text-gray-500 leading-snug line-clamp-2">
            {athlete.bio}
          </p>
        )}

        {/* iter579 · "Open Flythouse on iPhone" pill — web-only.
            Sits right below the athlete's bio so anyone visiting the
            profile from a shared link sees a natural upgrade path
            into the native experience. */}
        <div className="pt-2">
          <AppStoreBadge variant="dark" size="sm" testid={`athlete-profile-app-store-badge-${athlete.slug}`} />
        </div>
      </div>

      {/* Basics editor modal — opens when owner taps the hero photo. */}
      {isOwnerRaw && (
        <BasicsEditorModal
          open={showBasicsModal}
          onClose={() => setShowBasicsModal(false)}
          athlete={athlete}
          onSaved={(patch) => {
            setAthlete((a) => ({ ...(a || {}), ...patch }));
            setShowBasicsModal(false);
          }}
        />
      )}

      {/* iter233 · LIVE GAME PILL — sits between the profile header
          and the sticky tab strip. Renders as a liquid-glass Apple UI
          scoreboard when the athlete's linked college team is playing
          live, or a dimmed "next game" pill during the week. Hidden
          entirely when the athlete has no linked school, no upcoming
          game, or we're outside the sport's season. */}
      <LiveGamePill
        school={athlete.school_slug || athlete.school_name || athlete.school || ''}
        initialGame={liveGame}
      />

      {/* === STICKY TAB STRIP — matches /profile page styling === */}
      {!isVisionary && (
        <section className="sticky top-0 z-20 bg-black/95 backdrop-blur-md border-y border-white/10" data-testid="player-profile-tabs">
          <div className="max-w-3xl md:max-w-5xl lg:max-w-6xl mx-auto flex overflow-x-auto no-scrollbar">
            <ProfileTab id="story"    active={activeTab} onClick={setActiveTab} icon={Sparkles}  label="Story" />
            <ProfileTab id="career"   active={activeTab} onClick={setActiveTab} icon={Trophy}    label="Career"  count={(athlete.career_milestones || []).length} />
            <ProfileTab id="stories"  active={activeTab} onClick={setActiveTab} icon={FileText}  label="Articles" count={articleItems.length} />
            <ProfileTab id="shorts"   active={activeTab} onClick={setActiveTab} icon={Film}      label="Shorts"   count={relatedShorts.length} />
            <ProfileTab id="stats"    active={activeTab} onClick={setActiveTab} icon={BarChart3} label="Stats"    count={(athlete.stats || []).length} />
          </div>
        </section>
      )}

      <div className="max-w-3xl md:max-w-5xl lg:max-w-6xl mx-auto px-4 sm:px-6 md:px-8 lg:px-10 py-8 sm:py-10 space-y-12">

        {/* === ARTICLES PANEL === */}
        {!isVisionary && activeTab === 'stories' && (
          <section data-testid="player-profile-stories">
            <div className="flex items-center justify-between mb-3">
              <p className="font-italic text-xs tracking-[0.25em] uppercase text-blue-300">All Articles About {athlete.name}</p>
              {isOwner && (
                <button
                  type="button"
                  onClick={() => setShowAddArticleModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full ring-1 ring-blue-400/40 text-blue-200 text-[10px] uppercase tracking-widest font-bold hover:bg-blue-500/10 transition"
                  data-testid="add-article-btn"
                >
                  <Plus className="w-3 h-3" /> Add article
                </button>
              )}
            </div>
            {(() => {
              // iter519 · `articleItems` (memoized above) is the single
              // source of truth for both this panel AND the tab count.
              // See the useMemo definition for dedupe rules.
              const combined = articleItems;
              if (combined.length === 0) {
                return (
                  <EmptyTab
                    icon={FileText}
                    message={
                      isOwner
                        ? `No articles yet. Tap "+ Add article" to paste any URL that mentions you — we'll pull the thumbnail + source automatically.`
                        : `No articles featuring ${athlete.name} yet. Check back soon — new pieces drop weekly.`
                    }
                  />
                );
              }
              return (
                <div
                  className="flex gap-3 overflow-x-auto -mx-4 px-4 sm:-mx-6 sm:px-6 md:-mx-8 md:px-8 pb-2 scrollbar-hide snap-x snap-mandatory"
                  data-testid="profile-articles-rail"
                >
                  {combined.map((item) => {
                    const CardEl = item.external ? 'a' : Link;
                    const props = item.external
                      ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' }
                      : { to: item.href };
                    // Fallback source label used only when no thumbnail is available.
                    // We derive it from the URL host if the outlet field is empty so
                    // the dark-gradient fallback never reads "Sourced by".
                    const sourceLabel = item.outlet
                      || (() => {
                        try {
                          if (!item.external) return 'Flythouse';
                          const host = new URL(item.href).hostname.replace(/^www\./, '');
                          const root = host.split('.')[0];
                          if (!root) return 'Article';
                          // Short acronyms uppercased so we don't render "Espn".
                          if (root.length <= 4 && !root.includes('-')) return root.toUpperCase();
                          return root.charAt(0).toUpperCase() + root.slice(1);
                        } catch {
                          return 'Article';
                        }
                      })();
                    const resolvedImage = item.image ? resolveImageUrl(item.image) : '';
                    return (
                      <CardEl
                        key={item.key}
                        {...props}
                        className="group flex-shrink-0 w-56 sm:w-64 snap-start rounded-2xl overflow-hidden ring-1 ring-white/10 bg-white/[0.04] hover:ring-blue-400/60 transition"
                        data-testid={item.slug ? `profile-story-${item.slug}` : `profile-press-${item.key}`}
                      >
                        <div className="relative aspect-video bg-black overflow-hidden">
                          {resolvedImage ? (
                            <img
                              src={resolvedImage}
                              alt=""
                              loading="lazy"
                              referrerPolicy="no-referrer"
                              className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition duration-500"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                const fb = e.currentTarget.nextElementSibling;
                                if (fb) fb.style.opacity = '1';
                              }}
                            />
                          ) : null}
                          {/* Dark-gradient fallback — source name only when no image */}
                          <div
                            className="absolute inset-0 flex flex-col items-center justify-center px-3 text-center transition-opacity"
                            style={{
                              background:
                                'radial-gradient(120% 100% at 50% 0%, rgba(35,35,42,0.85) 0%, rgba(10,10,12,0.98) 60%, #05050a 100%)',
                              opacity: resolvedImage ? 0 : 1,
                            }}
                            aria-hidden={resolvedImage ? 'true' : 'false'}
                          >
                            <p className="text-[9px] uppercase tracking-[0.3em] font-black text-white/45">
                              {sourceLabel}
                            </p>
                          </div>
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
                        </div>
                        <div className="px-2.5 py-2">
                          <p className="text-white text-xs font-semibold leading-tight line-clamp-2">
                            {item.headline}
                          </p>
                          {item.date && (
                            <p className="text-[9px] uppercase tracking-widest text-white/40 mt-1">
                              {item.date}
                            </p>
                          )}
                        </div>
                      </CardEl>
                    );
                  })}
                </div>
              );
            })()}
          </section>
        )}

        {/* === CAREER PANEL (Phase 1) === */}
        {!isVisionary && activeTab === 'career' && (
          <section data-testid="player-profile-career">
            {(athlete.career_milestones || []).length === 0 ? (
              <EmptyTab icon={Trophy} message={`No career milestones for ${athlete.name} yet. Check back — the story's still being written.`} />
            ) : (
              <RichCareerTimeline athlete={athlete} />
            )}
          </section>
        )}

        {/* === SHORTS PANEL === */}
        {!isVisionary && activeTab === 'shorts' && (
          <section data-testid="player-profile-shorts">
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="font-italic text-xs tracking-[0.25em] uppercase text-blue-300">Shorts & Reels Featuring {athlete.name}</p>
              {isOwner && relatedShorts.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setShortsEditMode((v) => !v);
                    setShortsSelected(new Set());
                  }}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-bold ring-1 transition shrink-0 ${
                    shortsEditMode
                      ? 'bg-white text-black ring-white'
                      : 'bg-white/5 text-white/70 ring-white/10 hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid="shorts-edit-toggle"
                >
                  {shortsEditMode ? 'Done' : 'Edit'}
                </button>
              )}
            </div>
            {relatedShorts.length === 0 ? (
              <EmptyTab icon={Film} message={`No shorts on ${athlete.name} yet — FLYT Studios is filming.`} />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {relatedShorts.map(v => {
                  // iter815 · Every shorts entry has its own `id`
                  // regardless of source — no longer gated on `asset_id`
                  // so an admin/demo short is selectable too.
                  const shortKey = v.asset_id || v.id;
                  const canRemove = shortsEditMode;
                  const selected = shortKey && shortsSelected.has(shortKey);
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => {
                        if (canRemove) {
                          setShortsSelected((prev) => {
                            const next = new Set(prev);
                            if (next.has(shortKey)) next.delete(shortKey);
                            else next.add(shortKey);
                            return next;
                          });
                        } else {
                          setActiveVideo({
                            title: v.title,
                            subtitle: v.subtitle,
                            video_url: v.video_url,
                            poster: v.poster,
                            body: v.body,
                            variant: 'short',
                          });
                        }
                      }}
                      className={`group block aspect-[9/16] rounded-2xl overflow-hidden bg-black ring-1 hover:ring-blue-400/60 transition relative w-full text-left ${
                        canRemove ? 'ring-2 ' + (selected ? 'ring-red-500' : 'ring-white/10 hover:ring-white/40') : 'ring-white/10'
                      }`}
                      data-testid={`profile-short-${v.id}`}
                    >
                      {v.poster ? (
                        <img src={resolveImageUrl(v.poster, 400)} alt={v.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                      ) : null}
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                      {canRemove ? (
                        <span
                          className={`absolute top-2 right-2 w-7 h-7 rounded-full ring-2 ring-white flex items-center justify-center transition ${
                            selected ? 'bg-red-500' : 'bg-black/50'
                          }`}
                        >
                          {selected ? (
                            <X className="w-4 h-4 text-white" strokeWidth={3} />
                          ) : (
                            <span className="w-2.5 h-2.5 rounded-full bg-white/40" />
                          )}
                        </span>
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                            <Play className="w-5 h-5 fill-black text-black ml-0.5" />
                          </div>
                        </div>
                      )}
                      <div className="absolute bottom-2 left-2 right-2">
                        <p className="font-display text-sm leading-tight line-clamp-2">{v.title}</p>
                        {v.subtitle && <p className="text-[10px] text-gray-300 line-clamp-1 mt-0.5">{v.subtitle}</p>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            {shortsEditMode && shortsSelected.size > 0 && (
              <div className="mt-4 flex items-center justify-between gap-3 p-3 rounded-2xl bg-red-500/10 ring-1 ring-red-500/30">
                <p className="text-xs text-red-100">
                  <span className="font-black">{shortsSelected.size}</span> selected
                </p>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={handleArchiveSelectedFromShorts}
                    disabled={shortsRemoving || shortsDeleting}
                    title="Pulls these off your public profile — stays in your Media Hub folder"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-60 text-white text-[10px] uppercase tracking-widest font-black transition"
                    data-testid="shorts-archive-selected"
                  >
                    {shortsRemoving ? 'Archiving…' : 'Archive'}
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteSelectedShorts}
                    disabled={shortsRemoving || shortsDeleting}
                    title="Deletes everywhere — public profile AND your Media Hub folder"
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-500 hover:bg-red-400 disabled:opacity-60 text-white text-[10px] uppercase tracking-widest font-black transition"
                    data-testid="shorts-delete-selected"
                  >
                    {shortsDeleting ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* === STATS PANEL === */}
        {!isVisionary && activeTab === 'stats' && (
          <section data-testid="player-profile-stats">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <p className="font-italic text-xs tracking-[0.25em] uppercase text-blue-300">
                {athlete.stats_season ? `${athlete.stats_season} Stats` : 'Season Stats'}
              </p>
              {athlete.espn_synced_at && (
                <p className="text-[10px] uppercase tracking-widest text-gray-500">
                  FLYT Sync · {new Date(athlete.espn_synced_at).toLocaleDateString()}
                </p>
              )}
            </div>
            {(athlete.stats || []).length === 0 ? (
              <EmptyTab icon={BarChart3} message="Stats coming soon. We auto-sync directly from ESPN for the leagues we cover." />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {athlete.stats.map((s, i) => (
                  <div key={`s-${i}`} className="bg-gradient-to-br from-blue-500/10 to-white/[0.02] ring-1 ring-white/10 rounded-2xl px-4 py-5 text-center">
                    <p className="font-display text-4xl sm:text-5xl text-white leading-none">{s.value}</p>
                    <p className="text-[10px] uppercase tracking-widest text-gray-400 mt-2">{s.label}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {activeTab === 'stats' && (athlete.stats || []).length > 0 && (
          <section className="-mt-2" data-testid="athlete-stats-strip">
            <p className="font-italic text-xs tracking-[0.25em] uppercase text-blue-300 mb-3">
              {athlete.stats_season ? `${athlete.stats_season} · Quick Stats` : 'Quick Stats'}
            </p>
            <div className={`grid gap-2 sm:gap-3 ${
              athlete.stats.length >= 5 ? 'grid-cols-3 sm:grid-cols-5'
                : athlete.stats.length === 4 ? 'grid-cols-2 sm:grid-cols-4'
                : athlete.stats.length === 3 ? 'grid-cols-3'
                : athlete.stats.length === 2 ? 'grid-cols-2'
                : 'grid-cols-1'
            }`}>
              {athlete.stats.map((s, i) => (
                <div
                  key={`${s.label}-${i}`}
                  className="bg-white/[0.04] ring-1 ring-white/10 rounded-2xl px-3 py-3 sm:px-4 sm:py-4 text-center"
                  data-testid={`athlete-stat-${i}`}
                >
                  <p className="font-display text-2xl sm:text-3xl text-white leading-none">
                    {s.value}
                  </p>
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 mt-1.5">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {activeTab === 'story' && (
          <>
        {/* CAREER TIMELINE used to live up here near the top — it now
            renders at the bottom of the profile as a "résumé at a glance"
            outro. See the matching block below the chapters. */}

        {/* CAREER TIMELINE — sits at the TOP of the profile, right
            above the hero video, so the journey is the first thing the
            viewer reads after the name + tags. Renders with a single
            milestone too. Phase 1: auto-map the new rich schema
            ({date, title, ...}) → the horizontal-rail schema
            ({year, label, image, stats}) so both viewers work off the
            same underlying `career_milestones` list. */}
        {(athlete.career_milestones || []).length >= 1 && (
          <CareerTimeline
            milestones={(athlete.career_milestones || []).map((m) => {
              // Already in rail-schema? pass through.
              if (m.year || m.label) return m;
              const year = m.date ? String(m.date).slice(0, 4) : '';
              // Convert stats dict → array of {label, value} the rail expects.
              const statsArr = m.stats && typeof m.stats === 'object' && !Array.isArray(m.stats)
                ? Object.entries(m.stats).map(([k, v]) => ({ label: k.replace(/_/g, ' ').toUpperCase(), value: String(v) }))
                : Array.isArray(m.stats) ? m.stats : [];
              return {
                year,
                label: m.title,
                image: (m.photos || [])[0],
                stats: statsArr,
                current: !!m.pinned,
              };
            })}
          />
        )}

        {/* VIDEO — accepts any YouTube or Vimeo share/watch/embed/Shorts
            URL and auto-converts to the embed form. Falls back to a raw
            <video> tag for uploaded MP4s (/api/files/...) or other direct
            video URLs. */}
        {athlete.network_hero_video && (() => {
          const url = athlete.network_hero_video;
          const ytId = _extractYouTubeId(url);
          const embed = toVideoEmbed(url);
          const aspect = getVideoAspectClass(url);
          const direct = isDirectVideo(url);
          return (
            <section data-testid="athlete-network-video">
              {ytId ? (
                // iter346 · Native-safe YouTube — on Capacitor iOS,
                // the raw <iframe src="youtube.com/embed/..."> fails
                // because YouTube rejects the capacitor://localhost
                // origin. YouTubeEmbed renders a thumbnail + tap-to-
                // open-in-app on native; iframe on web.
                <YouTubeEmbed videoId={ytId} watchUrl={url} title={`${athlete.name} feature`} className={aspect} />
              ) : embed ? (
                <div className={`${aspect} rounded-2xl overflow-hidden bg-black ring-1 ring-white/10`}>
                  <iframe src={embed} title={`${athlete.name} feature`}
                    className="w-full h-full" frameBorder="0"
                    scrolling="no"
                    loading="lazy"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen />
                </div>
              ) : direct ? (
                <div className={`${aspect} rounded-2xl overflow-hidden bg-black ring-1 ring-white/10`}>
                  <video src={resolveSrc(url)} controls playsInline
                    className="w-full h-full" preload="metadata" />
                </div>
              ) : (
                <VideoLinkFallbackCard url={url} title={`${athlete.name} feature`} aspect={aspect} />
              )}
            </section>
          );
        })()}

        {/* ADDITIONAL VIDEOS — admin-curated list under the hero (sit-downs,
            BTS, alt cuts). Each entry is its own player block with a title
            + optional one-line description. Empty/invalid rows are
            skipped. */}
        {Array.isArray(athlete.network_videos) && athlete.network_videos.filter(v => v && v.url).length > 0 && (
          <section data-testid="athlete-network-extra-videos" className="space-y-6">
            {athlete.network_videos.filter(v => v && v.url).map((v, i) => {
              const ytId = _extractYouTubeId(v.url);
              const embed = toVideoEmbed(v.url);
              const aspect = getVideoAspectClass(v.url);
              const direct = isDirectVideo(v.url);
              return (
                <div key={v._uid || `extra-vid-${i}`} className="space-y-2" data-testid={`extra-video-${i}`}>
                  {v.title && (
                    <h3 className="font-display text-lg sm:text-xl uppercase tracking-tight text-white">{v.title}</h3>
                  )}
                  {ytId ? (
                    <YouTubeEmbed
                      videoId={ytId}
                      watchUrl={v.url}
                      title={v.title || `${athlete.name} video ${i + 1}`}
                      className={aspect}
                    />
                  ) : embed ? (
                    <div className={`${aspect} rounded-2xl overflow-hidden bg-black ring-1 ring-white/10`}>
                      <iframe src={embed} title={v.title || `${athlete.name} video ${i + 1}`}
                        className="w-full h-full" frameBorder="0"
                        scrolling="no"
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen />
                    </div>
                  ) : direct ? (
                    <div className={`${aspect} rounded-2xl overflow-hidden bg-black ring-1 ring-white/10`}>
                      <video src={resolveSrc(v.url)} controls playsInline
                        className="w-full h-full" preload="metadata" />
                    </div>
                  ) : (
                    <VideoLinkFallbackCard url={v.url} title={v.title || `${athlete.name} video ${i + 1}`} aspect={aspect} />
                  )}
                  {v.description && (
                    <p className="text-sm text-gray-400 leading-relaxed">{v.description}</p>
                  )}
                </div>
              );
            })}
          </section>
        )}

        {/* STORY BODY — pulled from linked story if available, else custom network_body */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-blue-400" />
            <p className="font-display text-sm uppercase tracking-widest text-blue-300">The Story</p>
          </div>
          {body && (
            <p className="text-lg lg:text-xl text-gray-300 leading-relaxed font-body mb-6 whitespace-pre-line">{body}</p>
          )}
        </section>

        {/* THE REASON — why we sat down with them */}
        {athlete.story_reason && (
          <section className="bg-blue-500/5 border border-blue-500/20 rounded-3xl p-6 lg:p-10" data-testid="story-reason">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                <Quote className="w-5 h-5 text-blue-300" />
              </div>
              <div>
                <p className="font-display text-xs uppercase tracking-widest text-blue-300 mb-3">The Reason</p>
                <p className="text-gray-200 text-lg lg:text-xl leading-relaxed whitespace-pre-line">{athlete.story_reason}</p>
              </div>
            </div>
          </section>
        )}

        {/* CHAPTERS — FLYT case-study style sections (image + body, alternating) */}
        {athlete.network_chapters?.length > 0 && (
          <div className="space-y-16 lg:space-y-24" data-testid="network-chapters">
            {athlete.network_chapters.map((chapter, idx) => (
              <NetworkChapter key={chapter.title || chapter.image || `chapter-${idx}`} chapter={chapter} idx={idx} />
            ))}
          </div>
        )}

        {/* BRAND PARTNERS / WHAT THEY USE */}
        {athlete.brand_partners?.length > 0 && (
          <section data-testid="athlete-brand-strip">
            <div className="flex items-center gap-2 mb-5">
              <ShoppingBag className="w-4 h-4 text-blue-400" />
              <p className="font-display text-sm uppercase tracking-widest text-blue-300">In Their World — Brands & Drops</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {athlete.brand_partners.map((p, i) => (
                <a key={p.url || p.label || `brand-${i}`} href={safeHref(p.url)} target="_blank" rel="noopener noreferrer"
                  className="group flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-blue-400/60 hover:bg-white/10 transition"
                  data-testid={`brand-partner-${i}`}>
                  <div className="w-14 h-14 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    {p.image ? <img loading="lazy" decoding="async" src={resolveImageUrl(p.image, 160)} alt={p.label} className="w-full h-full object-cover rounded-xl" /> : <ShoppingBag className="w-5 h-5 text-blue-300" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-white truncate">{p.label}</p>
                    {p.note && <p className="text-xs text-gray-400 mt-0.5 truncate">{p.note}</p>}
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-blue-300 flex-shrink-0" />
                </a>
              ))}
            </div>
          </section>
        )}

        {/* iter668h · Merged: Inside the World now hosts the entire
            career archive teaser — the Netflix-style catalog (photos,
            highlights, docs, articles, everything) lives one tap away
            behind the "Open" button. Grid is still the athlete-approved
            `approved_gallery` (populated exclusively by "Push to profile"
            from their Media Hub), but capped is bumped so a whole
            game shows without feeling stubby. Owners see an inline
            Edit toggle to prune any photo back off the public gallery. */}
        {(isOwner || (athlete.approved_gallery || []).length > 0 || athlete.has_career_media) && (
          <section data-testid="athlete-photo-gallery">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2 min-w-0">
                <p className="font-display text-sm uppercase tracking-widest text-blue-300 whitespace-nowrap">Inside the World</p>
                <span className="h-px flex-1 bg-white/10 min-w-4" />
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {isOwner && (athlete.approved_gallery || []).length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setGalleryEditMode((v) => !v);
                      setGallerySelected(new Set());
                    }}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-bold ring-1 transition ${
                      galleryEditMode
                        ? 'bg-white text-black ring-white'
                        : 'bg-white/5 text-white/70 ring-white/10 hover:bg-white/10 hover:text-white'
                    }`}
                    data-testid="gallery-edit-toggle"
                  >
                    {galleryEditMode ? 'Done' : 'Edit'}
                  </button>
                )}
                <Link
                  to={`/network/${athlete.slug}/media`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white text-black text-[10px] uppercase tracking-widest font-black hover:bg-blue-100 transition"
                  data-testid="inside-world-open"
                >
                  Open
                </Link>
              </div>
            </div>

            {(athlete.approved_gallery || []).length === 0 ? (
              <div className="rounded-2xl bg-white/[0.03] ring-1 ring-white/10 p-8 text-center">
                <p className="text-sm text-white/50">
                  {isOwner
                    ? 'Push photos from your Media Hub to feature them here.'
                    : 'The full archive lives one tap away.'}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2 md:gap-3">
                  {athlete.approved_gallery.slice(0, 6).map((src, i) => {
                    const selected = gallerySelected.has(src);
                    return (
                      <button
                        key={`g-${i}`}
                        type="button"
                        onClick={() => {
                          if (galleryEditMode) {
                            setGallerySelected((prev) => {
                              const next = new Set(prev);
                              if (next.has(src)) next.delete(src);
                              else next.add(src);
                              return next;
                            });
                          } else {
                            // iter668i · Non-edit taps open the shared
                            // FLYT liquid-glass viewer instead of
                            // punting the visitor out to the raw
                            // Cloudinary URL.
                            setLightboxUrl(src);
                          }
                        }}
                        className={`relative block aspect-square overflow-hidden rounded-xl bg-white/5 transition ${
                          galleryEditMode ? 'ring-2 ' + (selected ? 'ring-red-500' : 'ring-white/10 hover:ring-white/40') : ''
                        }`}
                        data-testid={`athlete-photo-gallery-tile-${i}`}
                      >
                        <img
                          src={resolveImageUrl(src, 400)}
                          alt={`${athlete.name} ${i + 1}`}
                          className="w-full h-full object-cover pointer-events-none"
                          loading="lazy"
                          draggable={false}
                        />
                        {galleryEditMode && (
                          <span
                            className={`absolute top-1.5 right-1.5 w-6 h-6 rounded-full ring-2 ring-white flex items-center justify-center transition ${
                              selected ? 'bg-red-500' : 'bg-black/50'
                            }`}
                          >
                            {selected ? (
                              <X className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                            ) : (
                              <span className="w-2 h-2 rounded-full bg-white/40" />
                            )}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {galleryEditMode && gallerySelected.size > 0 && (
                  <div className="mt-4 flex items-center justify-between gap-3 p-3 rounded-2xl bg-red-500/10 ring-1 ring-red-500/30">
                    <p className="text-xs text-red-100">
                      <span className="font-black">{gallerySelected.size}</span> selected — will be pulled off your public profile.
                    </p>
                    <button
                      type="button"
                      onClick={handleRemoveSelectedFromGallery}
                      disabled={galleryRemoving}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-500 hover:bg-red-400 disabled:opacity-60 text-white text-[10px] uppercase tracking-widest font-black transition"
                      data-testid="gallery-remove-selected"
                    >
                      {galleryRemoving ? 'Removing…' : 'Remove'}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* EXTERNAL / SOCIAL LINKS */}
        {athlete.external_links?.length > 0 && (
          <section>
            <p className="font-display text-sm uppercase tracking-widest text-blue-300 mb-4">Follow</p>
            <div className="flex flex-wrap gap-2">
              {athlete.external_links.map((l, i) => (
                <a key={l.url || l.label || `ext-${i}`} href={safeHref(l.url)} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:border-blue-400/60 text-sm transition">
                  {l.label.toLowerCase().includes('insta') && <Instagram className="w-3.5 h-3.5" />}
                  {l.label}
                </a>
              ))}
            </div>
          </section>
        )}
          </>
        )}

        {/* CAREER TIMELINE — now rendered at the TOP of the profile,
            right above the hero video (see block earlier in this file). */}

        {/* Phase 2A · Digital Home sections — Awards, Academics, Recruiting,
            Media Kit, Press, Events, Bookings, Fan Club, Newsletter, Merch,
            Game Film. Owners see empty-state "Unlock +X score" prompts;
            public viewers only see populated sections. */}
        {!isVisionary && athlete && (
          <>
            <DigitalHomeAnchorNav athlete={athlete} isOwner={isOwner} />
            <AthleteDigitalHomeSections
              athlete={athlete}
              isOwner={isOwner}
              onEdit={setEditorSection}
            />
            {/* iter668h · Career Media gateway merged into the
                "Inside the World" section higher up on the profile —
                single Open button feeds the same /network/:slug/media
                route so there's no duplicate CTA. */}
          </>
        )}

        {/* CTA — back to network + share to story */}
        <section className="border-t border-white/10 pt-12 text-center">
          <p className="font-italic text-blue-300 text-sm tracking-wider uppercase mb-3">Keep watching</p>
          <h2 className="font-display text-2xl md:text-3xl uppercase mb-5">{isVisionary ? 'More Dreamers on the Network' : 'More Athletes on the Network'}</h2>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Link to={isVisionary ? '/visionary/network#athletes' : '/#athletes'} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black font-bold hover:bg-gray-200 transition" data-testid="browse-network-btn">
              Browse the Network <Play className="w-4 h-4 fill-black" />
            </Link>
            <ShareToStoryButton
              title={athlete.name}
              subtitle={athlete.network_tagline || athlete.story_reason || athlete.bio}
              category={athlete.sport}
              image={resolveImageUrl(athlete.network_hero_image || athlete.photo)}
              url={`https://${SHARE_DOMAIN}${isVisionary ? '/visionary/network' : '/network'}/${athlete.slug}`}
              channelId={isVisionary ? 'dreams_visionary' : 'dreams_league'}
            />
          </div>
        </section>
      </div>

      {/* Video modal — opens when a Short card is tapped. Prevents the
          freeze caused by `<a href={video_url} target="_blank">` on mobile
          when the URL is a raw /api/files video that the browser can't
          stream inline. */}
      <NetworkVideoModal
        video={activeVideo}
        athlete={activeVideo ? { id: athlete.id, slug: athlete.slug, name: athlete.name } : null}
        variant={activeVideo?.variant || 'wide'}
        onClose={() => setActiveVideo(null)}
      />

      {/* Phase 2A · Inline section editor drawer — only opens for owners
          via the "Edit" / "Add" buttons on their own profile sections. */}
      {isOwner && (
        <AthleteSectionEditorDrawer
          athlete={athlete}
          sectionId={editorSection}
          onClose={() => setEditorSection(null)}
          onSaved={(fresh) => { setAthlete(fresh); }}
        />
      )}

      {/* Phase 1 · Claim Profile modal — mounted at the root so the
          overlay covers the full viewport. Triggered by the "Claim
          Profile" CTA under the info bar. */}
      {showClaimModal && (
        <ClaimProfileModal
          athlete={athlete}
          onClose={() => setShowClaimModal(false)}
          onClaimed={() => {
            // Optimistically flip status so the CTA disappears + pending pill appears
            setAthlete((a) => ({ ...a, claim_status: 'claimed' }));
          }}
        />
      )}

      {/* iter473 · Network Profile onboarding tour — renders exactly
          once per account. Gate + trigger lives in the effect above
          this render tree. */}
      <NetworkProfileTour
        open={tourOpen}
        steps={tourSteps}
        onComplete={finishTour}
        onSkipAll={finishTour}
      />

      {/* iter55 / iter341 · FLYT ID™ promo — bottom-of-profile CTA.
          Rules (revised — `auto_provisioned` isn't reliably set on
          older imports, so use `claim_status` as the source of truth):
            1. Hide for the profile owner themselves.
            2. Hide when the athlete is ALREADY claimed / verified.
               `claim_status === 'verified'` or `'claimed'` means the
               person made an account with us — "Claim this profile"
               is nonsense. Same for any profile with a linked
               `public_user_id`.
            3. Show for stub profiles where `claim_status` is
               `'public'` (or missing) AND there's no linked user —
               genuine claim candidates (Baylor / SMU / ESPN imports). */}
      {(() => {
        const isOwner = user?.linked_athlete === athlete?.slug && athlete?.claim_status === 'verified';
        const alreadyClaimed = !!(athlete?.public_user_id || athlete?.linked_public_user_id);
        const claimStatus = athlete?.claim_status || 'public';
        const isVerified = claimStatus === 'verified' || claimStatus === 'claimed';
        const isClaimableStub = athlete && !alreadyClaimed && !isVerified;
        if (!athlete || isOwner || !isClaimableStub) return null;
        return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
          <FlytLinkPromoCard
            variant="unclaimed"
            onCta={() => {
              if (!athlete.claim_status || athlete.claim_status === 'public') {
                if (athlete?.espn_id) {
                  const params = new URLSearchParams({
                    name: athlete.name || '',
                    sport: athlete.sport || '',
                    position: athlete.position || '',
                    jersey: athlete.jersey || '',
                    class: athlete.class_year || '',
                    height: athlete.height || '',
                    weight: athlete.weight || '',
                    school_slug: athlete.school_slug || '',
                    school_name: athlete.school_name || athlete.team || '',
                  });
                  navigate(`/claim/espn/${encodeURIComponent(athlete.espn_id)}?${params.toString()}`);
                } else {
                  setShowClaimModal(true);
                }
              } else {
                window.location.assign('/profile?tab=integrate');
              }
            }}
            secondaryHref={`/network/jaxon`}
            testid="athlete-profile-flyt-link-promo"
          />
        </div>
        );
      })()}

      {/* Owner-only quick editor for Hudl / MaxPreps / Opendorse.
          Same PUT endpoint as the /athlete/me dashboard so both
          surfaces stay in sync. Local `athlete` state is patched on
          save so the branded pills refresh without a page reload. */}
      <AthletePlatformsModal
        open={showPlatformsModal}
        athleteSlug={athlete?.slug}
        initial={{
          hudl_url:      athlete?.hudl_url || '',
          maxpreps_url:  athlete?.maxpreps_url || '',
          opendorse_url: athlete?.opendorse_url || '',
        }}
        onClose={() => setShowPlatformsModal(false)}
        onSaved={(patch) => {
          setAthlete((a) => (a ? { ...a, ...patch } : a));
        }}
      />

      {/* iter126 · Athlete-facing "paste any article URL → save to profile" modal */}
      <AddArticleModal
        open={showAddArticleModal}
        onClose={() => setShowAddArticleModal(false)}
        existingPress={athlete?.press || []}
        onSaved={(updated) => {
          // Merge updated athlete doc back into local state so the tab
          // re-renders with the new article without a page reload.
          setAthlete((a) => (a ? { ...a, ...updated } : a));
        }}
      />
      {/* /iter459s max-w-3xl inner clamp */}
      </div>
      <PhotoLightbox
        open={!!lightboxUrl}
        url={lightboxUrl}
        alt={athlete?.name || 'Photo'}
        onClose={() => setLightboxUrl(null)}
      />
    </div>
  );
};

export default AthleteNetworkPage;

// === Profile tab — underline style matching the user /profile page ===
const ProfileTab = ({ id, active, onClick, icon: Icon, label, count }) => {
  const isActive = active === id;
  const showCount = typeof count === 'number';
  return (
    <button
      type="button"
      onClick={() => { if (!isActive) { hapticLight(); } onClick(id); }}
      className={`flex-1 min-w-[100px] py-3.5 flex items-center justify-center gap-1.5 text-[11px] sm:text-xs font-bold uppercase tracking-wider border-b-2 transition whitespace-nowrap px-2 ${
        isActive ? 'border-blue-400 text-white' : 'border-transparent text-gray-400 hover:text-white'
      }`}
      data-testid={`profile-tab-${id}`}
    >
      <Icon className="w-4 h-4" />
      {label}
      {showCount && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isActive ? 'bg-blue-500/20 text-blue-300' : 'bg-white/10 text-gray-400'}`}>
          {count}
        </span>
      )}
    </button>
  );
};

// === Empty-state block for tabs with nothing to show yet ===
const EmptyTab = ({ icon: Icon, message }) => (
  <div className="py-14 text-center bg-white/[0.03] ring-1 ring-white/10 rounded-2xl">
    <Icon className="w-8 h-8 text-gray-600 mx-auto mb-3" />
    <p className="text-sm text-gray-400 max-w-xs mx-auto">{message}</p>
  </div>
);

// === Live game badge — shown in the hero when the athlete's team is
// currently in an ESPN-live game. Re-uses the 60s scoreboard cache so this
// adds no extra fetch latency. Pulses red, links to the scoreboard. ===
const LiveGameBadge = ({ game, athleteName }) => {
  const team = game.athlete_team || {};
  const opp = game.opponent || {};
  const isHome = game.athlete_side === 'home';
  const teamScore = isHome ? game.score?.home : game.score?.away;
  const oppScore  = isHome ? game.score?.away : game.score?.home;
  return (
    <div
      className="inline-flex items-center gap-3 mb-4 px-3.5 py-2 rounded-full bg-red-500/15 ring-1 ring-red-400/40 backdrop-blur-sm"
      data-testid="live-game-badge"
      role="status"
      aria-label={`${athleteName} is playing right now`}
    >
      <span className="relative flex h-2.5 w-2.5">
        <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
      </span>
      <span className="font-display text-[10px] uppercase tracking-[0.25em] text-red-300">Live · {game.league}</span>
      <span className="hidden sm:inline-flex items-center gap-2 text-sm text-white font-bold">
        {team.shortName || team.name}
        <span className="text-lg tabular-nums">{teamScore ?? '-'}</span>
        <span className="text-gray-400 text-xs">{isHome ? 'vs' : '@'}</span>
        {opp.shortName || opp.name}
        <span className="text-lg tabular-nums">{oppScore ?? '-'}</span>
      </span>
      <span className="text-[11px] uppercase tracking-widest text-gray-300">{game.status_text}</span>
    </div>
  );
};

// === Chapter renderer — FLYT case-study style sections ===
const NetworkChapter = ({ chapter, idx }) => {
  const layout = chapter.layout || 'image_right';
  // Only treat a chapter image as "present" if the field is a non-empty
  // string. Production rows occasionally store empty / whitespace / null
  // strings that still pass !!chapter.image — those rendered as broken
  // ALT-text tiles in the screenshot the user reported. Same hardening
  // is applied to the gallery `images` array.
  const validImage = typeof chapter.image === 'string' && chapter.image.trim().length > 0
    ? chapter.image.trim()
    : null;
  const validImages = (chapter.images || []).filter(s => typeof s === 'string' && s.trim().length > 0);
  const hasImage = !!validImage;
  const hasGallery = validImages.length > 0;
  // If a chapter has *no* title, *no* body, and *no* media we just skip
  // it entirely — it's a stub the admin probably left behind. Stops the
  // "CHAPTER 01: THE FISH TANKS" empty card from showing on production.
  const isEmpty = !chapter.title && !chapter.body && !hasImage && !hasGallery;
  if (isEmpty) return null;

  if (layout === 'text_only') {
    return (
      <section data-testid={`chapter-${idx}`}>
        <p className="font-display text-xs uppercase tracking-widest text-blue-300 mb-2">Chapter {String(idx + 1).padStart(2, '0')}</p>
        <h3 className="font-display text-3xl md:text-4xl uppercase leading-tight mb-5">{chapter.title}</h3>
        <p className="text-gray-300 text-lg leading-relaxed whitespace-pre-line">{chapter.body}</p>
      </section>
    );
  }

  if (layout === 'image_full') {
    return (
      <section data-testid={`chapter-${idx}`}>
        <p className="font-display text-xs uppercase tracking-widest text-blue-300 mb-2">Chapter {String(idx + 1).padStart(2, '0')}</p>
        <h3 className="font-display text-3xl md:text-4xl uppercase leading-tight mb-5">{chapter.title}</h3>
        {hasImage && (
          <div className="aspect-video rounded-2xl overflow-hidden bg-black mb-5 ring-1 ring-white/10">
            <img src={resolveImageUrl(validImage, 400)} alt={chapter.title} loading="lazy" decoding="async" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }} />
          </div>
        )}
        <p className="text-gray-300 text-lg leading-relaxed whitespace-pre-line">{chapter.body}</p>
        {hasGallery && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6">
            {validImages.map((src, i) => (
              <div key={src || `chap-img-${i}`} className="aspect-square rounded-xl overflow-hidden bg-white/5">
                <img src={resolveImageUrl(src, 400)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }} />
              </div>
            ))}
          </div>
        )}
      </section>
    );
  }

  // image_left or image_right (default)
  const imgFirst = layout === 'image_left';
  return (
    <section className="grid md:grid-cols-2 gap-6 lg:gap-10 items-center" data-testid={`chapter-${idx}`}>
      <div className={imgFirst ? 'order-1' : 'order-1 md:order-2'}>
        {hasImage ? (
          <div className="aspect-[4/5] md:aspect-square rounded-2xl overflow-hidden bg-black ring-1 ring-white/10">
            <img src={resolveImageUrl(validImage, 400)} alt={chapter.title} loading="lazy" decoding="async" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }} />
          </div>
        ) : null}
        {hasGallery && (
          <div className="grid grid-cols-2 gap-2 mt-3">
            {validImages.slice(0, 2).map((src, i) => (
              <div key={src || `chap-pre-${i}`} className="aspect-video rounded-lg overflow-hidden bg-white/5">
                <img src={resolveImageUrl(src, 400)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }} />
              </div>
            ))}
          </div>
        )}
      </div>
      <div className={imgFirst ? 'order-2' : 'order-2 md:order-1'}>
        <p className="font-display text-xs uppercase tracking-widest text-blue-300 mb-2">Chapter {String(idx + 1).padStart(2, '0')}</p>
        <h3 className="font-display text-3xl md:text-4xl uppercase leading-tight mb-5">{chapter.title}</h3>
        <p className="text-gray-300 text-base lg:text-lg leading-relaxed whitespace-pre-line">{chapter.body}</p>
      </div>
    </section>
  );
};